import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { salesApi, outletsApi, partnersApi } from '../lib/api';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import {
  MagnifyingGlass, Receipt, CurrencyInr, Bank, Tag, Scales,
  CheckCircle, WarningCircle, XCircle, CaretLeft, CaretRight, CaretDown,
  Clock, ChartBar, Storefront, Funnel, X, Package,
  TrendUp, TrendDown, Lightning, ArrowsDownUp, Info, Handshake,
} from '@phosphor-icons/react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, Cell, PieChart, Pie,
  LineChart, Line, Legend,
} from 'recharts';
import TimeOfDayBento from './TimeOfDayBento';
import ItemsByHourView from './sales-explorer/ItemsByHourView';
import DateRangeFilter from '../components/DateRangeFilter';
import DeltaPill from '../components/DeltaPill';
import PartnerIntelligencePanel from '../components/PartnerIntelligencePanel';
import { resolveInitialRange, isValidISO, fmtDDMMYYYY } from '../lib/dateRange';

const fmtCurrency = (v) =>
  v != null ? Number(v).toLocaleString('en-IN', { maximumFractionDigits: 2 }) : '0';

const fmtShort = (v) => {
  if (!v && v !== 0) return '0';
  if (v >= 100000) return `${(v / 100000).toFixed(1)}L`;
  if (v >= 1000) return `${(v / 1000).toFixed(1)}K`;
  return Number(v).toFixed(0);
};

const TABS = [
  { key: 'orders', label: 'Orders', icon: Receipt },
  { key: 'items', label: 'Items', icon: Package },
  { key: 'hourly', label: 'Time of Day', icon: Clock },
  { key: 'partners', label: 'Partners', icon: Handshake },
];


/* ── Grain Awareness Badge ── */
const GrainBadge = ({ grainSummary, grainNote }) => {
  const [showTip, setShowTip] = useState(false);
  if (!grainSummary || !grainSummary.is_mixed_grain) return null;
  return (
    <div className="relative inline-flex items-center gap-1">
      <span
        className="text-[10px] px-2 py-0.5 rounded-full bg-amber-500/10 text-amber-400 border border-amber-500/20 cursor-help font-medium tracking-wide"
        onMouseEnter={() => setShowTip(true)}
        onMouseLeave={() => setShowTip(false)}
        data-testid="grain-badge"
      >
        Mixed Grain Data
      </span>
      {showTip && (
        <div className="absolute z-50 bottom-full left-0 mb-1.5 w-72 p-2.5 rounded-lg bg-zinc-900 border border-white/10 shadow-xl text-[11px] text-zinc-300 leading-relaxed" data-testid="grain-tooltip">
          {grainNote || `Includes ${grainSummary.day_records} daily and ${grainSummary.month_records} monthly aggregated item records. Daily analytics exclude monthly aggregates.`}
        </div>
      )}
    </div>
  );
};

/* ── Day-Only Grain Note ── */
const DayOnlyNote = ({ grainSummary, grainNote }) => {
  if (!grainSummary?.is_mixed_grain) return null;
  return (
    <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-blue-500/5 border border-blue-500/10 text-[11px] text-blue-300/80" data-testid="day-only-note">
      <Info size={13} className="shrink-0" />
      <span>{grainNote || 'This view uses daily data only. Monthly aggregates are excluded.'}</span>
    </div>
  );
};

const SalesExplorerPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  // ── URL-driven date range (shared contract with Sales Overview) ──
  // Single source of truth: `date_from` / `date_to` URL params.
  // Default landing range is THIS MONTH when params are missing
  // (centralised in `resolveInitialRange` so every page agrees).
  const initialRange = resolveInitialRange(searchParams);
  const [dateFrom, setDateFrom] = useState(initialRange.from);
  const [dateTo,   setDateTo]   = useState(initialRange.to);

  const handleRangeChange = (from, to) => {
    if (!isValidISO(from) || !isValidISO(to)) {
      toast.error('Invalid dates'); return;
    }
    setDateFrom(from); setDateTo(to);
  };

  const [outlets, setOutlets] = useState([]);
  const [orders, setOrders] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [expandedId, setExpandedId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [activeTab, setActiveTab] = useState(() => {
    // Honour `?tab=items|orders|hourly|partners` on initial mount so
    // cross-page deep-links (e.g. Sales Overview Top Items card)
    // land on the right tab without a flash.
    const t = searchParams.get('tab');
    return ['orders', 'items', 'hourly', 'partners'].includes(t) ? t : 'orders';
  });

  // Filters
  const [filterOutlet, setFilterOutlet] = useState('');
  const [filterChannel, setFilterChannel] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [searchQuery, setSearchQuery] = useState('');

  // Analytics
  const [hourlyData, setHourlyData] = useState([]);
  const [channelData, setChannelData] = useState([]);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);

  // Partners
  const [partners, setPartners] = useState([]);
  const [filterPartner, setFilterPartner] = useState('');
  const [partnerSummary, setPartnerSummary] = useState(null);
  const [partnerTrends, setPartnerTrends] = useState(null);
  // org_id resolved once during partner fetch — reused by the
  // Partner Intelligence Panel so it doesn't have to re-resolve.
  const [resolvedOrgId, setResolvedOrgId] = useState(null);
  const [partnerLoading, setPartnerLoading] = useState(false);
  const [hiddenPartners, setHiddenPartners] = useState(new Set());

  // Item Intelligence
  const [itemsLoading, setItemsLoading] = useState(false);
  const [topItems, setTopItems] = useState(null);
  const [itemFilters, setItemFilters] = useState(null);
  const [itemSortBy, setItemSortBy] = useState('quantity');
  const [itemBucket, setItemBucket] = useState('');
  const [itemCategory, setItemCategory] = useState('');
  const [itemSubView, setItemSubView] = useState(() => {
    // Deep-link: `?item=<normalized>` lands on the item detail drawer.
    return searchParams.get('item') ? 'detail' : 'top';
  });
  const [categoryData, setCategoryData] = useState(null);
  const [moversData, setMoversData] = useState(null);
  const [selectedItem, setSelectedItem] = useState(() => searchParams.get('item') || null);
  const [itemDetail, setItemDetail] = useState(null);
  const [itemSearch, setItemSearch] = useState('');
  const [dailyTrendData, setDailyTrendData] = useState(null);
  const [weekdayData, setWeekdayData] = useState(null);
  const [txnContext, setTxnContext] = useState(null);

  // Ops KPIs (Overview strip inside Orders tab)
  const [opsKpis, setOpsKpis] = useState(null);

  // Sync URL whenever the applied range / tab / drill-in changes —
  // no localStorage stickiness, so a shared link always reproduces
  // the same view (date range + selected tab + open item drawer).
  // Placed AFTER all referenced state vars so we avoid a TDZ
  // ReferenceError on the dep array during the first render pass.
  useEffect(() => {
    const sp = new URLSearchParams(searchParams);
    sp.set('date_from', dateFrom);
    sp.set('date_to', dateTo);
    if (activeTab && activeTab !== 'orders') sp.set('tab', activeTab);
    else sp.delete('tab');
    if (activeTab === 'items' && selectedItem) sp.set('item', selectedItem);
    else sp.delete('item');
    setSearchParams(sp, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateFrom, dateTo, activeTab, selectedItem]);

  const LIMIT = 30;

  useEffect(() => {
    Promise.all([
      outletsApi.list(),
      partnersApi.list('active'),
    ]).then(([outletsRes, partnersRes]) => {
      setOutlets(outletsRes.data);
      setPartners(partnersRes.data);
    }).catch(() => { toast.error('Failed to load outlets/partners'); });
  }, []);

  // Build the shared time params (`date_from` / `date_to`) sent to all
  // analytics endpoints. This is the SINGLE place where the active
  // date range turns into request params.
  const getTimeParams = useCallback(() => ({
    date_from: dateFrom,
    date_to: dateTo,
  }), [dateFrom, dateTo]);

  const fetchOrders = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: LIMIT, offset: page * LIMIT };
      if (filterOutlet) params.outlet_id = filterOutlet;
      // Unified date contract: always pass the range; the backend
      // collapses identical from/to into a single-day match. When the
      // user picks `Today`, the call becomes ?date_from=YYYY-MM-DD&
      // date_to=YYYY-MM-DD — same effect as the legacy single-day input.
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo)   params.date_to   = dateTo;
      if (filterChannel) params.channel = filterChannel;
      if (filterStatus) params.status = filterStatus;
      if (searchQuery) params.search = searchQuery;
      const res = await salesApi.listOrders(params);
      setOrders(res.data.orders || []);
      setTotal(res.data.total || 0);
    } catch (err) {
      toast.error('Failed to load orders');
    } finally { setLoading(false); }
  }, [page, filterOutlet, dateFrom, dateTo, filterChannel, filterStatus, searchQuery]);

  const fetchAnalytics = useCallback(async () => {
    setAnalyticsLoading(true);
    try {
      const params = {};
      if (filterOutlet) params.outlet_id = filterOutlet;
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo)   params.date_to   = dateTo;
      const [hourlyRes, channelRes] = await Promise.all([
        salesApi.getHourlyAnalytics(params),
        salesApi.getChannelAnalytics(params),
      ]);
      setHourlyData(hourlyRes.data || []);
      setChannelData(channelRes.data || []);
    } catch (err) { toast.error('Failed to load analytics'); }
    finally { setAnalyticsLoading(false); }
  }, [filterOutlet, dateFrom, dateTo]);

  useEffect(() => { fetchOrders(); }, [fetchOrders]);
  useEffect(() => { if (activeTab === 'hourly') fetchAnalytics(); }, [activeTab, fetchAnalytics]);

  // Ops KPIs — fetch for Orders tab overview strip
  const fetchOpsKpis = useCallback(async () => {
    try {
      const { default: apiClient } = await import('../lib/api');
      const params = new URLSearchParams();
      if (filterOutlet) params.append('outlet_id', filterOutlet);
      // time-intelligence uses start_date/end_date (Phase 5.5 convention).
      if (dateFrom) params.append('start_date', dateFrom);
      if (dateTo)   params.append('end_date',   dateTo);
      const res = await apiClient.get(`/sales/time-intelligence?${params.toString()}`);
      setOpsKpis(res.data?.ops_kpis || null);
    } catch {
      setOpsKpis(null);
    }
  }, [filterOutlet, dateFrom, dateTo]);
  useEffect(() => { if (activeTab === 'orders') fetchOpsKpis(); }, [activeTab, fetchOpsKpis]);

  // Partner data fetch
  const fetchPartnerData = useCallback(async () => {
    setPartnerLoading(true);
    try {
      const me = JSON.parse(localStorage.getItem('1os_user_info') || '{}');
      // Get org_id from /auth/me 
      let orgId = me.org_id;
      if (!orgId) {
        try {
          const meRes = await salesApi.getDailySummary(); // triggers auth, org comes from access
          orgId = meRes?.data?.org_id;
        } catch { /* ignore */ }
      }
      if (!orgId) {
        // Fallback: fetch from auth/me
        const { data: authMe } = await import('../lib/api').then(m => m.default.get('/auth/me'));
        orgId = authMe.org_id;
      }
      const params = { org_id: orgId };
      if (filterOutlet) params.outlet_id = filterOutlet;
      if (dateFrom && dateTo) {
        params.date_from = dateFrom;
        params.date_to   = dateTo;
      }
      const [summaryRes, trendsRes] = await Promise.all([
        partnersApi.getSummary({ ...params, include_comparison: true }),
        partnersApi.getTrends(params),
      ]);
      setPartnerSummary(summaryRes.data);
      setPartnerTrends(trendsRes.data);
      setResolvedOrgId(orgId);
    } catch (err) { toast.error('Failed to load partner data'); }
    finally { setPartnerLoading(false); }
  }, [filterOutlet, dateFrom, dateTo]);

  useEffect(() => { if (activeTab === 'partners') fetchPartnerData(); }, [activeTab, fetchPartnerData]);

  // Item intelligence fetch
  const fetchItemData = useCallback(async () => {
    setItemsLoading(true);
    try {
      const timeParams = getTimeParams();
      const params = { ...timeParams };
      if (filterOutlet) params.outlet_id = filterOutlet;
      if (itemBucket) params.revenue_bucket = itemBucket;
      if (itemCategory) params.category = itemCategory;
      if (itemSearch) params.search = itemSearch;
      if (filterPartner) params.partner_id = filterPartner;

      if (itemSubView === 'top') {
        params.sort_by = itemSortBy;
        params.limit = 30;
        params.include_comparison = true;
        const [topRes, filtersRes] = await Promise.all([
          salesApi.getTopItems(params),
          itemFilters ? Promise.resolve({ data: itemFilters }) : salesApi.getItemFilters({ outlet_id: filterOutlet || undefined }),
        ]);
        setTopItems(topRes.data);
        if (!itemFilters) setItemFilters(filtersRes.data);
      } else if (itemSubView === 'categories') {
        const catParams = { ...params, group_by: 'category', include_comparison: true };
        const res = await salesApi.getCategoryAnalytics(catParams);
        setCategoryData(res.data);
      } else if (itemSubView === 'movers') {
        const res = await salesApi.getMovers({ ...params, include_comparison: true });
        setMoversData(res.data);
      } else if (itemSubView === 'trends') {
        const { search: _s, ...trendParams } = params;
        const res = await salesApi.getDailyItemTrend(trendParams);
        setDailyTrendData(res.data);
      } else if (itemSubView === 'weekday') {
        const { search: _s, ...wParams } = params;
        const res = await salesApi.getWeekdayAnalytics(wParams);
        setWeekdayData(res.data);
      } else if (itemSubView === 'detail' && selectedItem) {
        const { search: _s, ...detailParams } = params;
        const [trendRes, ctxRes] = await Promise.all([
          salesApi.getItemTrends(selectedItem, detailParams),
          salesApi.getTransactionContext({ outlet_id: filterOutlet || undefined }),
        ]);
        setItemDetail(trendRes.data);
        setTxnContext(ctxRes.data);
      }
    } catch (err) { toast.error('Failed to load item data'); }
    finally { setItemsLoading(false); }
  }, [itemSubView, itemSortBy, itemBucket, itemCategory, itemSearch, filterOutlet, filterPartner, selectedItem, itemFilters, getTimeParams]);

  useEffect(() => { if (activeTab === 'items') fetchItemData(); }, [activeTab, fetchItemData]);

  const handleExpand = async (order) => {
    if (expandedId === order.id) { setExpandedId(null); setDetail(null); return; }
    setExpandedId(order.id);
    try {
      const res = await salesApi.getOrder(order.id);
      setDetail(res.data);
    } catch (err) { setDetail(null); toast.error('Failed to load order detail'); }
  };

  const clearFilters = () => {
    setFilterOutlet(''); setFilterChannel('');
    setFilterStatus(''); setSearchQuery(''); setPage(0);
    setItemBucket(''); setItemCategory(''); setItemSearch('');
    setFilterPartner('');
  };

  const drillIntoItem = (itemNorm) => {
    setSelectedItem(itemNorm);
    setItemSubView('detail');
  };

  const backToTopItems = () => {
    setSelectedItem(null);
    setItemDetail(null);
    setItemSubView('top');
  };

  const hasFilters = filterOutlet || filterChannel || filterStatus || searchQuery || filterPartner;
  const hasItemFilters = itemBucket || itemCategory || itemSearch || filterPartner;
  const totalPages = Math.ceil(total / LIMIT);

  return (
    <div className="space-y-5" data-testid="sales-explorer-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-medium font-['Outfit'] tracking-tight">Sales Explorer</h1>
          <p className="text-sm text-zinc-400 mt-1" data-testid="explorer-subtitle">
            Unified view across all canonical sales truth sources · {fmtDDMMYYYY(dateFrom)} → {fmtDDMMYYYY(dateTo)}
          </p>
        </div>
        <DateRangeFilter
          value={{ from: dateFrom, to: dateTo }}
          onChange={handleRangeChange}
          testIdPrefix="explorer-range"
        />
      </div>

      {/* Tab bar */}
      <div className="flex gap-1 bg-white/[0.02] rounded-lg p-1 w-fit" data-testid="explorer-tabs">
        {TABS.map((t) => {
          const Icon = t.icon;
          const active = activeTab === t.key;
          return (
            <button
              key={t.key}
              onClick={() => setActiveTab(t.key)}
              className={`flex items-center gap-1.5 px-4 py-2 rounded-md text-xs font-medium transition-colors ${active ? 'bg-white/10 text-white' : 'text-zinc-500 hover:text-zinc-300'}`}
              data-testid={`tab-${t.key}`}
            >
              <Icon size={14} />
              {t.label}
            </button>
          );
        })}
      </div>

      {/* Global Filters (Orders/Hourly/Channels tabs) */}
      {activeTab !== 'items' && (
        <div className="surface-base rounded-lg p-4">
          <div className="flex items-center gap-3 flex-wrap">
            <div className="flex items-center gap-1.5 bg-[#18181B] border border-white/10 rounded-md px-3 py-1.5 flex-1 min-w-[200px] max-w-xs">
              <MagnifyingGlass size={14} className="text-zinc-500" />
              <input
                type="text"
                placeholder="Search order ID, channel..."
                value={searchQuery}
                onChange={(e) => { setSearchQuery(e.target.value); setPage(0); }}
                className="bg-transparent text-xs text-zinc-200 outline-none flex-1 placeholder:text-zinc-600"
                data-testid="search-input"
              />
            </div>
            <Select value={filterOutlet || '__ALL__'} onValueChange={(v) => { setFilterOutlet(v === '__ALL__' ? '' : v); setPage(0); }}>
              <SelectTrigger className="bg-[#18181B] border-white/10 h-8 text-xs w-[160px]" data-testid="filter-outlet">
                <SelectValue placeholder="All outlets" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__ALL__">All outlets</SelectItem>
                {outlets.map((o) => <SelectItem key={o.id} value={o.id}>{o.display_name || o.id}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={filterStatus || '__ALL__'} onValueChange={(v) => { setFilterStatus(v === '__ALL__' ? '' : v); setPage(0); }}>
              <SelectTrigger className="bg-[#18181B] border-white/10 h-8 text-xs w-[120px]" data-testid="filter-status">
                <SelectValue placeholder="All statuses" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__ALL__">All statuses</SelectItem>
                <SelectItem value="PASS">PASS</SelectItem>
                <SelectItem value="WARN">WARN</SelectItem>
                <SelectItem value="FAIL">FAIL</SelectItem>
              </SelectContent>
            </Select>
            <Select value={filterPartner || '__ALL__'} onValueChange={(v) => { setFilterPartner(v === '__ALL__' ? '' : v); setPage(0); }}>
              <SelectTrigger className="bg-[#18181B] border-white/10 h-8 text-xs w-[140px]" data-testid="filter-partner">
                <SelectValue placeholder="All partners" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__ALL__">All partners</SelectItem>
                {partners.filter(p => p.partner_id !== 'unknown').map((p) => (
                  <SelectItem key={p.partner_id} value={p.partner_id}>{p.partner_name}</SelectItem>
                ))}
                <SelectItem value="unknown">Unknown</SelectItem>
              </SelectContent>
            </Select>
            {hasFilters && (
              <button onClick={clearFilters} className="flex items-center gap-1 text-xs text-zinc-500 hover:text-zinc-300 transition-colors" data-testid="clear-filters">
                <X size={12} /> Clear
              </button>
            )}
            <div className="ml-auto text-xs text-zinc-500 font-mono" data-testid="order-count">
              {total} order{total !== 1 ? 's' : ''}
            </div>
          </div>
        </div>
      )}

      {/* ── ORDERS TAB ── */}
      {activeTab === 'orders' && (
        <>
          {/* Ops KPI Strip (Avg Delivery Prep + Avg Table Turnaround) */}
          {opsKpis && (opsKpis.avg_delivery_prep_min != null || opsKpis.avg_dine_in_turnaround_min != null) && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4" data-testid="ops-kpi-strip">
              <div className="surface-base rounded-lg p-3 border border-white/[0.06]">
                <div className="text-[10px] uppercase tracking-wider text-zinc-500 flex items-center gap-1">
                  <Clock size={11} weight="fill" className="text-blue-400" />
                  Avg Delivery Prep Time
                </div>
                <div className="mt-1 flex items-baseline gap-1.5">
                  <span className="text-lg font-mono text-zinc-100" data-testid="kpi-delivery-prep">
                    {opsKpis.avg_delivery_prep_min != null ? `${Math.round(opsKpis.avg_delivery_prep_min)}` : '—'}
                  </span>
                  <span className="text-[10px] text-zinc-500">min</span>
                  <span className="ml-auto text-[10px] font-mono text-zinc-600">
                    {opsKpis.avg_delivery_sample || 0} orders
                  </span>
                </div>
              </div>
              <div className="surface-base rounded-lg p-3 border border-white/[0.06]">
                <div className="text-[10px] uppercase tracking-wider text-zinc-500 flex items-center gap-1">
                  <Clock size={11} weight="fill" className="text-emerald-400" />
                  Avg Table Turnaround
                </div>
                <div className="mt-1 flex items-baseline gap-1.5">
                  <span className="text-lg font-mono text-zinc-100" data-testid="kpi-dine-turnaround">
                    {opsKpis.avg_dine_in_turnaround_min != null ? `${Math.round(opsKpis.avg_dine_in_turnaround_min)}` : '—'}
                  </span>
                  <span className="text-[10px] text-zinc-500">min</span>
                  <span className="ml-auto text-[10px] font-mono text-zinc-600">
                    {opsKpis.avg_dine_in_sample || 0} orders
                  </span>
                </div>
              </div>
              <div className="surface-base rounded-lg p-3 border border-white/[0.06] flex items-center justify-between">
                <div className="text-[11px] text-zinc-500 leading-relaxed">
                  Computed from <span className="text-zinc-400 font-mono">invoiceDate − createdDate</span>.<br />
                  Excludes outliers &gt; 360 min and zero-duration rows.
                </div>
              </div>
            </div>
          )}
          {loading ? (
            <div className="py-16 text-center text-zinc-500">Loading orders...</div>
          ) : orders.length === 0 ? (
            <div className="surface-base rounded-lg py-16 text-center">
              <Receipt size={48} className="mx-auto text-zinc-700 mb-4" />
              <p className="text-zinc-500 text-sm">No orders found</p>
              {hasFilters && <button onClick={clearFilters} className="mt-3 text-xs text-[#D4FF00] hover:underline">Clear filters</button>}
            </div>
          ) : (
            <div className="space-y-2" data-testid="orders-list">
              {orders.map((o) => (
                <OrderRow
                  key={o.id}
                  order={o}
                  expanded={expandedId === o.id}
                  detail={expandedId === o.id ? detail : null}
                  onToggle={() => handleExpand(o)}
                />
              ))}
            </div>
          )}
          {totalPages > 1 && (
            <div className="flex items-center justify-center gap-3">
              <button onClick={() => setPage(Math.max(0, page - 1))} disabled={page === 0} className="p-2 rounded bg-white/5 hover:bg-white/10 disabled:opacity-30 transition-colors" data-testid="prev-page">
                <CaretLeft size={16} className="text-zinc-300" />
              </button>
              <span className="text-xs text-zinc-500 font-mono">{page + 1} / {totalPages}</span>
              <button onClick={() => setPage(Math.min(totalPages - 1, page + 1))} disabled={page >= totalPages - 1} className="p-2 rounded bg-white/5 hover:bg-white/10 disabled:opacity-30 transition-colors" data-testid="next-page">
                <CaretRight size={16} className="text-zinc-300" />
              </button>
            </div>
          )}
        </>
      )}

      {/* ── HOURLY TAB (Time of Day Bento) ── */}
      {activeTab === 'hourly' && (
        <TimeOfDayBento outletId={filterOutlet} startDate={dateFrom} endDate={dateTo} />
      )}

      {/* ── PARTNERS TAB ── */}
      {activeTab === 'partners' && (
        <div className="space-y-4" data-testid="partners-tab">
          {partnerLoading ? (
            <div className="py-16 text-center text-zinc-500">Loading partner data...</div>
          ) : !partnerSummary?.partners?.length ? (
            <div className="surface-base rounded-lg py-16 text-center text-zinc-500">
              <Handshake size={48} className="mx-auto text-zinc-700 mb-4" />
              <p>No partner data yet. Upload transactions to see partner insights.</p>
            </div>
          ) : (
            <>
              {/* ── Partner Intelligence Panel ─────────────────
                  Renders whenever there's partner data:
                    • Single partner selected → that partner's slice.
                    • "All partners" (filter cleared) → an aggregated
                      view across every partner in the window so the
                      operator can read the outlet as a whole without
                      switching tabs.
                  Composes existing endpoints — no new APIs. */}
              {(() => {
                if (!partnerSummary?.partners?.length) return null;
                let sel, name, pid;
                if (filterPartner) {
                  sel  = partnerSummary.partners.find(p => p.partner_id === filterPartner);
                  if (!sel) return null;
                  const meta = partners.find(pm => pm.partner_id === filterPartner);
                  pid  = filterPartner;
                  name = meta?.partner_name || filterPartner;
                } else {
                  // Build an aggregated "All partners" pseudo-summary
                  // by summing every partner row. service_mode_breakdown
                  // is merged across partners by service_mode. Prev-
                  // window fields are summed the same way so the panel
                  // can render outlet-level deltas consistently.
                  const rows = partnerSummary.partners;
                  const modeMap = {};
                  for (const r of rows) {
                    for (const m of (r.service_mode_breakdown || [])) {
                      const cur = modeMap[m.service_mode] || {
                        service_mode: m.service_mode,
                        orders: 0, net_sales: 0, covers: 0,
                        previous_orders: 0, previous_net_sales: 0,
                      };
                      cur.orders             += m.orders || 0;
                      cur.net_sales          += m.net_sales || 0;
                      cur.covers             += m.covers || 0;
                      cur.previous_orders    += m.previous_orders || 0;
                      cur.previous_net_sales += m.previous_net_sales || 0;
                      modeMap[m.service_mode] = cur;
                    }
                  }
                  const total_net      = rows.reduce((a, r) => a + (r.net_sales || 0), 0);
                  const prev_total_net = rows.reduce((a, r) => a + (r.previous_net_sales || 0), 0);
                  const _pct = (cur, prev) => {
                    if (!(prev > 0)) return cur > 0 ? { pct: null, dir: 'new' } : { pct: 0, dir: 'flat' };
                    const p = Math.round((cur - prev) / prev * 1000) / 10;
                    return { pct: p, dir: p > 0.5 ? 'up' : p < -0.5 ? 'down' : 'flat' };
                  };
                  const _pp = (cur, prev) => {
                    const v = Math.round(((cur || 0) - (prev || 0)) * 10) / 10;
                    return { pp: v, dir: v > 0.1 ? 'up' : v < -0.1 ? 'down' : 'flat' };
                  };
                  const mode_arr = Object.values(modeMap)
                    .sort((a, b) => b.net_sales - a.net_sales)
                    .map(m => {
                      const share_cur  = total_net > 0      ? (m.net_sales / total_net * 100) : 0;
                      const share_prev = prev_total_net > 0 ? (m.previous_net_sales / prev_total_net * 100) : 0;
                      const revD = _pct(m.net_sales, m.previous_net_sales);
                      const shD  = _pp(share_cur, share_prev);
                      return {
                        ...m,
                        share_pct: Math.round(share_cur * 10) / 10,
                        previous_share_pct: Math.round(share_prev * 10) / 10,
                        revenue_change_pct:        revD.pct,
                        revenue_change_direction:  revD.dir,
                        share_change_pp:           shD.pp,
                        share_change_direction:    shD.dir,
                      };
                    });
                  const sum = (k) => rows.reduce((a, r) => a + (r[k] || 0), 0);
                  const orders      = sum('orders');
                  const prev_orders = sum('previous_orders');
                  const gross_cur   = sum('gross_sales');
                  const gross_prev  = sum('previous_gross_sales');
                  const disc_cur    = sum('discounts');
                  const disc_prev   = sum('previous_discounts');
                  const covers_cur  = rows.reduce((a, r) => a + (r.total_covers || 0), 0);
                  const covers_prev = rows.reduce((a, r) => a + (r.previous_total_covers || 0), 0);
                  const aov_cur     = orders      > 0 ? total_net      / orders      : 0;
                  const aov_prev    = prev_orders > 0 ? prev_total_net / prev_orders : 0;
                  const drate_cur   = gross_cur   > 0 ? (disc_cur  / gross_cur)  * 100 : 0;
                  const drate_prev  = gross_prev  > 0 ? (disc_prev / gross_prev) * 100 : 0;
                  const revD = _pct(total_net, prev_total_net);
                  const ordD = _pct(orders, prev_orders);
                  const aovD = _pct(aov_cur, aov_prev);
                  const covD = _pct(covers_cur, covers_prev);
                  const drD  = _pp(drate_cur, drate_prev);
                  sel = {
                    partner_id: '__ALL__',
                    orders,
                    gross_sales:  Math.round(gross_cur * 100) / 100,
                    discounts:    Math.round(disc_cur * 100) / 100,
                    net_sales:    Math.round(total_net * 100) / 100,
                    taxes_total:  Math.round(sum('taxes_total') * 100) / 100,
                    charges_net:  Math.round(sum('charges_net') * 100) / 100,
                    final_total:  Math.round(sum('final_total') * 100) / 100,
                    avg_order_value: Math.round(aov_cur * 100) / 100,
                    contribution_pct: null,  // "—" in panel; share is 100% by definition
                    total_covers: covers_cur,
                    service_mode_breakdown: mode_arr,
                    // Prev-window + deltas (synthesized from per-partner rows).
                    previous_orders:           prev_orders,
                    previous_net_sales:        Math.round(prev_total_net * 100) / 100,
                    previous_gross_sales:      Math.round(gross_prev * 100) / 100,
                    previous_discounts:        Math.round(disc_prev * 100) / 100,
                    previous_total_covers:     covers_prev,
                    previous_avg_order_value:  Math.round(aov_prev * 100) / 100,
                    previous_contribution_pct: null,
                    previous_discount_rate_pct: Math.round(drate_prev * 100) / 100,
                    discount_rate_pct:         Math.round(drate_cur * 100) / 100,
                    revenue_change_pct: revD.pct, revenue_change_direction: revD.dir,
                    orders_change_pct:  ordD.pct, orders_change_direction:  ordD.dir,
                    aov_change_pct:     aovD.pct, aov_change_direction:     aovD.dir,
                    covers_change_pct:  covD.pct, covers_change_direction:  covD.dir,
                    contribution_change_pp:  null, contribution_change_direction:  'flat',
                    discount_rate_change_pp: drD.pp, discount_rate_change_direction: drD.dir,
                    // Verdict / risks for outlet aggregate — keep null; the
                    // panel hides those blocks when not present, so the
                    // verdict / risk cards only render for single partners.
                    verdict: null,
                    risks: [],
                    narrative_channel: null,
                  };
                  pid  = '__ALL__';
                  name = 'All partners';
                }
                return (
                  <PartnerIntelligencePanel
                    partnerId={pid}
                    partnerName={name}
                    orgId={resolvedOrgId}
                    outletId={filterOutlet || undefined}
                    dateFrom={dateFrom}
                    dateTo={dateTo}
                    summary={sel}
                    trends={partnerTrends}
                    comparison={partnerSummary?.comparison}
                  />
                );
              })()}

              {/* Partner Summary Cards */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3" data-testid="partner-summary-cards">
                {partnerSummary.partners.map((p) => {
                  const partnerMeta = partners.find(pm => pm.partner_id === p.partner_id);
                  return (
                    <div key={p.partner_id} className="surface-base rounded-lg p-4 border border-white/5 hover:border-white/10 transition-colors" data-testid={`partner-card-${p.partner_id}`}>
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-2">
                          <div className={`w-2 h-2 rounded-full ${p.partner_id === 'inhouse' ? 'bg-emerald-400' : p.partner_id === 'unknown' ? 'bg-zinc-500' : 'bg-cyan-400'}`} />
                          <span className="text-sm font-medium text-zinc-200">{partnerMeta?.partner_name || p.partner_id}</span>
                        </div>
                        <span className="text-[10px] px-2 py-0.5 rounded-full bg-white/5 text-zinc-400 font-mono">
                          {partnerMeta?.partner_type || ''}
                        </span>
                      </div>
                      <div className="space-y-1.5">
                        <div className="flex justify-between text-xs">
                          <span className="text-zinc-500">Revenue</span>
                          <span className="font-mono text-zinc-200">{fmtCurrency(p.net_sales)}</span>
                        </div>
                        <div className="flex justify-between text-xs">
                          <span className="text-zinc-500">Orders</span>
                          <span className="font-mono text-zinc-300">{p.orders}</span>
                        </div>
                        <div className="flex justify-between text-xs">
                          <span className="text-zinc-500">AOV</span>
                          <span className="font-mono text-zinc-300">{fmtCurrency(p.avg_order_value)}</span>
                        </div>
                        <div className="flex justify-between text-xs">
                          <span className="text-zinc-500">Contribution</span>
                          <span className="font-mono text-[#D4FF00]">{p.contribution_pct}%</span>
                        </div>
                      </div>
                      {/* Contribution bar */}
                      <div className="mt-3 h-1.5 bg-white/5 rounded-full overflow-hidden">
                        <div className="h-full bg-[#D4FF00]/60 rounded-full transition-all" style={{ width: `${Math.min(p.contribution_pct, 100)}%` }} />
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Revenue by Partner — bento row layout */}
              <div className="surface-base rounded-lg p-5" data-testid="revenue-by-partner">
                <div className="flex items-baseline justify-between mb-4">
                  <h3 className="text-sm font-medium text-zinc-300 flex items-center gap-2">
                    <ChartBar size={16} className="text-cyan-400" /> Revenue by Partner
                  </h3>
                  <span className="text-[10px] uppercase tracking-widest text-zinc-600 font-mono">
                    {partnerSummary.partners.length} partner{partnerSummary.partners.length === 1 ? '' : 's'}
                  </span>
                </div>
                {(() => {
                  const sorted = [...partnerSummary.partners].sort((a, b) => (b.net_sales || 0) - (a.net_sales || 0));
                  const max = Math.max(...sorted.map(p => p.net_sales || 0), 1);
                  const dotCls = (pid) => {
                    if (pid === 'inhouse' || pid === 'inhouse_dine_in') return 'bg-emerald-400';
                    if (pid === 'unknown') return 'bg-zinc-500';
                    if (pid === 'zomato_delivery') return 'bg-red-500';
                    if (pid === 'zomato_dining')   return 'bg-red-300';
                    if (pid?.startsWith('zomato')) return 'bg-red-400';
                    if (pid === 'swiggy_delivery') return 'bg-orange-400';
                    if (pid === 'swiggy_dining')   return 'bg-yellow-400';
                    if (pid?.startsWith('swiggy')) return 'bg-orange-400';
                    return 'bg-cyan-400';
                  };
                  const barGradientCls = (pid) => {
                    if (pid === 'inhouse' || pid === 'inhouse_dine_in')
                      return 'bg-gradient-to-r from-emerald-500/60 to-emerald-400/30';
                    if (pid === 'zomato_delivery')
                      return 'bg-gradient-to-r from-red-600/60 to-red-500/20';
                    if (pid === 'zomato_dining')
                      return 'bg-gradient-to-r from-red-400/60 to-red-300/20';
                    if (pid?.startsWith('zomato'))
                      return 'bg-gradient-to-r from-red-500/60 to-red-400/20';
                    if (pid === 'swiggy_delivery')
                      return 'bg-gradient-to-r from-orange-500/60 to-orange-400/20';
                    if (pid === 'swiggy_dining')
                      return 'bg-gradient-to-r from-yellow-500/60 to-yellow-400/20';
                    if (pid?.startsWith('swiggy'))
                      return 'bg-gradient-to-r from-orange-500/60 to-orange-400/20';
                    return 'bg-gradient-to-r from-cyan-500/60 to-cyan-400/20';
                  };
                  return (
                    <div className="space-y-3" data-testid="revenue-by-partner-rows">
                      {sorted.map((p) => {
                        const meta = partners.find(pm => pm.partner_id === p.partner_id);
                        const name = meta?.partner_name || p.partner_id;
                        const pct = max > 0 ? Math.max((p.net_sales / max) * 100, 1.5) : 0;
                        return (
                          <div
                            key={p.partner_id}
                            className="group"
                            data-testid={`revenue-by-partner-row-${p.partner_id}`}
                          >
                            <div className="flex items-baseline justify-between mb-1.5">
                              <div className="flex items-center gap-2 min-w-0 pr-3">
                                <div className={`w-1.5 h-1.5 rounded-full shrink-0 ${dotCls(p.partner_id)}`} />
                                <span className="text-xs text-zinc-200 truncate">{name}</span>
                                {meta?.partner_type && (
                                  <span className="text-[9px] uppercase tracking-widest text-zinc-600 font-mono shrink-0">
                                    {meta.partner_type}
                                  </span>
                                )}
                              </div>
                              <div className="flex items-baseline gap-2 shrink-0 font-mono">
                                <span className="text-sm text-zinc-100">{fmtCurrency(p.net_sales)}</span>
                                <span className="text-[10px] text-zinc-500 tabular-nums w-9 text-right">{p.contribution_pct}%</span>
                              </div>
                            </div>
                            <div className="h-[5px] bg-white/[0.04] rounded-full overflow-hidden">
                              <div
                                className={`h-full rounded-full transition-all duration-500 ${barGradientCls(p.partner_id)} group-hover:brightness-125`}
                                style={{ width: `${pct}%` }}
                              />
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  );
                })()}
              </div>

              {/* Partner detail table */}
              <div className="overflow-x-auto">
                <table className="w-full text-xs" data-testid="partner-table">
                  <thead>
                    <tr className="bg-zinc-800/50 text-zinc-400 text-left">
                      <th className="p-3 font-mono">Partner</th>
                      <th className="p-3 font-mono">Type</th>
                      <th className="p-3 font-mono text-right">Orders</th>
                      <th className="p-3 font-mono text-right">Gross Sales</th>
                      <th className="p-3 font-mono text-right">Discounts</th>
                      <th className="p-3 font-mono text-right">Net Sales</th>
                      <th className="p-3 font-mono text-right">Taxes</th>
                      <th className="p-3 font-mono text-right">AOV</th>
                      <th className="p-3 font-mono text-right">Contribution</th>
                    </tr>
                  </thead>
                  <tbody>
                    {partnerSummary.partners.map((p) => {
                      const meta = partners.find(pm => pm.partner_id === p.partner_id);
                      return (
                        <tr key={p.partner_id} className="border-t border-white/5" data-testid={`partner-row-${p.partner_id}`}>
                          <td className="p-3 font-mono text-zinc-200">{meta?.partner_name || p.partner_id}</td>
                          <td className="p-3 text-zinc-500">{meta?.partner_type || '-'}</td>
                          <td className="p-3 font-mono text-right text-zinc-300">{p.orders}</td>
                          <td className="p-3 font-mono text-right text-zinc-300">{fmtCurrency(p.gross_sales)}</td>
                          <td className="p-3 font-mono text-right text-red-400/70">{p.discounts > 0 ? `-${fmtCurrency(p.discounts)}` : '0'}</td>
                          <td className="p-3 font-mono text-right text-zinc-200">{fmtCurrency(p.net_sales)}</td>
                          <td className="p-3 font-mono text-right text-zinc-300">{fmtCurrency(p.taxes_total)}</td>
                          <td className="p-3 font-mono text-right text-zinc-300">{fmtCurrency(p.avg_order_value)}</td>
                          <td className="p-3 font-mono text-right text-[#D4FF00]">{p.contribution_pct}%</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* Partner Trends — multi-line chart with rich hover card */}
              {partnerTrends?.trends?.length > 0 && (() => {
                const trends = partnerTrends.trends;
                // Pivot flat rows → one row per date with per-partner cells
                const byDate = {};
                const partnerIds = new Set();
                for (const t of trends) {
                  if (!byDate[t.date]) byDate[t.date] = { date: t.date };
                  byDate[t.date][`rev_${t.partner_id}`]  = t.net_sales;
                  byDate[t.date][`ord_${t.partner_id}`]  = t.orders;
                  partnerIds.add(t.partner_id);
                }
                const data = Object.values(byDate).sort((a, b) => a.date.localeCompare(b.date));
                // Order partners by their total revenue (legend + tooltip order)
                const totals = Array.from(partnerIds).map((pid) => ({
                  pid,
                  total: trends.filter(t => t.partner_id === pid).reduce((s, t) => s + (t.net_sales || 0), 0),
                }));
                totals.sort((a, b) => b.total - a.total);
                const orderedPartners = totals.map((t) => t.pid);

                // Colour mapping — mirrors the Revenue by Partner bento.
                // Sub-shades distinguish delivery vs dine-in inside each aggregator.
                const colorOf = (pid) => {
                  if (pid === 'inhouse' || pid === 'inhouse_dine_in') return '#34D399';
                  if (pid === 'unknown') return '#71717A';
                  if (pid === 'zomato_delivery') return '#EF4444'; // red-500, deeper
                  if (pid === 'zomato_dining')   return '#FCA5A5'; // red-300, lighter
                  if (pid?.startsWith('zomato')) return '#F87171';
                  if (pid === 'swiggy_delivery') return '#FB923C'; // orange-400
                  if (pid === 'swiggy_dining')   return '#FACC15'; // yellow-400
                  if (pid?.startsWith('swiggy')) return '#FB923C';
                  return '#22D3EE';
                };

                const nameOf = (pid) => {
                  const m = partners.find((p) => p.partner_id === pid);
                  return m?.partner_name || pid;
                };
                const shortDate = (iso) => {
                  if (!iso || iso.length < 10) return iso;
                  const d = new Date(iso);
                  return Number.isNaN(d.getTime()) ? iso :
                    d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });
                };
                const tooltipHeader = (iso) => {
                  if (!iso || iso.length < 10) return iso;
                  const d = new Date(iso);
                  if (Number.isNaN(d.getTime())) return iso;
                  const datePart = d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
                  const weekday  = d.toLocaleDateString('en-IN', { weekday: 'long' });
                  return `${datePart}, ${weekday}`;
                };

                const toggle = (pid) => {
                  setHiddenPartners((prev) => {
                    const next = new Set(prev);
                    if (next.has(pid)) next.delete(pid); else next.add(pid);
                    return next;
                  });
                };

                // Custom tooltip — partner | revenue | orders | AOV + Total row
                const RichTooltip = ({ active, label }) => {
                  if (!active || !label) return null;
                  const row = byDate[label];
                  if (!row) return null;
                  const rows = orderedPartners
                    .filter((pid) => !hiddenPartners.has(pid))
                    .map((pid) => {
                      const rev = row[`rev_${pid}`] || 0;
                      const ord = row[`ord_${pid}`] || 0;
                      return { pid, rev, ord, aov: ord > 0 ? rev / ord : 0 };
                    });
                  const totalRev = rows.reduce((s, r) => s + r.rev, 0);
                  const totalOrd = rows.reduce((s, r) => s + r.ord, 0);
                  const totalAov = totalOrd > 0 ? totalRev / totalOrd : 0;
                  return (
                    <div
                      className="min-w-[300px] max-w-[360px] rounded-lg border border-white/10 bg-[#0F0F12]/95 backdrop-blur px-4 py-3 shadow-xl"
                      data-testid="partner-trends-tooltip"
                    >
                      <div className="text-xs text-zinc-400 mb-2.5 font-medium">
                        {tooltipHeader(label)}
                      </div>
                      <div className="grid grid-cols-[1fr_auto_auto_auto] gap-x-3 gap-y-1.5 text-[11px] font-mono">
                        <div className="text-[9px] uppercase tracking-widest text-zinc-600">Partner</div>
                        <div className="text-[9px] uppercase tracking-widest text-zinc-600 text-right">Revenue</div>
                        <div className="text-[9px] uppercase tracking-widest text-zinc-600 text-right">Orders</div>
                        <div className="text-[9px] uppercase tracking-widest text-zinc-600 text-right">AOV</div>
                        {rows.map((r) => (
                          <React.Fragment key={r.pid}>
                            <div className="flex items-center gap-1.5 min-w-0">
                              <span
                                className="w-1.5 h-1.5 rounded-full shrink-0"
                                style={{ backgroundColor: colorOf(r.pid) }}
                              />
                              <span className="text-zinc-200 truncate">{nameOf(r.pid)}</span>
                            </div>
                            <div className="text-zinc-100 text-right tabular-nums">{r.rev > 0 ? `₹${fmtShort(r.rev)}` : '—'}</div>
                            <div className="text-zinc-300 text-right tabular-nums">{r.ord || '—'}</div>
                            <div className="text-zinc-400 text-right tabular-nums">{r.aov > 0 ? `₹${fmtShort(r.aov)}` : '—'}</div>
                          </React.Fragment>
                        ))}
                        <div className="col-span-4 border-t border-white/5 mt-1" />
                        <div className="text-zinc-500 uppercase tracking-widest text-[9px]">Total</div>
                        <div className="text-[#D4FF00] text-right tabular-nums">₹{fmtShort(totalRev)}</div>
                        <div className="text-zinc-200 text-right tabular-nums">{totalOrd}</div>
                        <div className="text-zinc-300 text-right tabular-nums">{totalAov > 0 ? `₹${fmtShort(totalAov)}` : '—'}</div>
                      </div>
                    </div>
                  );
                };

                return (
                  <div className="surface-base rounded-lg p-5" data-testid="partner-trends-chart">
                    <div className="flex items-baseline justify-between mb-4">
                      <h3 className="text-sm font-medium text-zinc-300 flex items-center gap-2">
                        <TrendUp size={16} className="text-emerald-400" /> Revenue Trend
                      </h3>
                      <span className="text-[10px] uppercase tracking-widest text-zinc-600 font-mono">
                        {data.length} day{data.length === 1 ? '' : 's'}
                      </span>
                    </div>

                    {/* Interactive legend — click to toggle a partner line */}
                    <div className="flex flex-wrap gap-x-4 gap-y-2 mb-4" data-testid="partner-trends-legend">
                      {orderedPartners.map((pid) => {
                        const hidden = hiddenPartners.has(pid);
                        return (
                          <button
                            key={pid}
                            type="button"
                            onClick={() => toggle(pid)}
                            className={`flex items-center gap-1.5 text-[11px] transition-opacity ${hidden ? 'opacity-35' : 'opacity-100'} hover:opacity-80`}
                            data-testid={`partner-trends-legend-${pid}`}
                            title={hidden ? 'Click to show' : 'Click to hide'}
                          >
                            <span
                              className="w-2 h-2 rounded-full"
                              style={{ backgroundColor: colorOf(pid) }}
                            />
                            <span className="text-zinc-300">{nameOf(pid)}</span>
                          </button>
                        );
                      })}
                    </div>

                    <div className="h-72">
                      <ResponsiveContainer width="100%" height="100%">
                        <LineChart data={data} margin={{ top: 8, right: 16, left: -8, bottom: 0 }}>
                          <CartesianGrid strokeDasharray="2 4" stroke="#27272A" vertical={false} />
                          <XAxis
                            dataKey="date"
                            stroke="#52525B"
                            fontSize={10}
                            tickMargin={8}
                            tickLine={false}
                            axisLine={{ stroke: '#27272A' }}
                            tickFormatter={shortDate}
                          />
                          <YAxis
                            stroke="#52525B"
                            fontSize={10}
                            tickLine={false}
                            axisLine={false}
                            tickFormatter={(v) => `₹${fmtShort(v)}`}
                            width={56}
                          />
                          <Tooltip
                            content={<RichTooltip />}
                            cursor={{ stroke: '#3F3F46', strokeWidth: 1, strokeDasharray: '3 3' }}
                            wrapperStyle={{ outline: 'none' }}
                          />
                          {orderedPartners.map((pid) => (
                            <Line
                              key={pid}
                              type="monotone"
                              dataKey={`rev_${pid}`}
                              stroke={colorOf(pid)}
                              strokeWidth={1.6}
                              dot={false}
                              activeDot={{ r: 3, strokeWidth: 0, fill: colorOf(pid) }}
                              isAnimationActive={false}
                              hide={hiddenPartners.has(pid)}
                              connectNulls
                            />
                          ))}
                        </LineChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                );
              })()}
            </>
          )}
        </div>
      )}

      {/* ── ITEMS TAB ── */}
      {activeTab === 'items' && (
        <div className="space-y-4" data-testid="items-tab">
          {/* Non-authoritative banner — revenue here is a line-level roll-up, see RCA note */}
          <div className="flex items-start gap-2 px-3 py-2 rounded-md bg-amber-500/5 border border-amber-500/15 text-[11px] text-amber-200/80" data-testid="items-revenue-caveat">
            <span className="uppercase tracking-widest text-amber-300/90 font-medium">For mix analysis</span>
            <span className="text-zinc-400">·</span>
            <span>Revenue on this tab is a line-level roll-up. Authoritative net sales lives in Overview / Transactions.</span>
          </div>
          {/* Item-specific filters (date range lives in the page header) */}
          <div className="surface-base rounded-lg p-4">
            <div className="flex items-center gap-3 flex-wrap">
              <div className="flex items-center gap-1.5 bg-[#18181B] border border-white/10 rounded-md px-3 py-1.5 min-w-[200px] max-w-xs">
                <MagnifyingGlass size={14} className="text-zinc-500" />
                <input
                  type="text"
                  placeholder="Search item name..."
                  value={itemSearch}
                  onChange={(e) => setItemSearch(e.target.value)}
                  className="bg-transparent text-xs text-zinc-200 outline-none flex-1 placeholder:text-zinc-600"
                  data-testid="item-search-input"
                />
              </div>

              <Select value={itemBucket || '__ALL__'} onValueChange={(v) => setItemBucket(v === '__ALL__' ? '' : v)}>
                <SelectTrigger className="bg-[#18181B] border-white/10 h-8 text-xs w-[140px]" data-testid="filter-bucket">
                  <SelectValue placeholder="All buckets" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__ALL__">All buckets</SelectItem>
                  {(itemFilters?.revenue_buckets || []).map((b) => <SelectItem key={b} value={b}>{b}</SelectItem>)}
                </SelectContent>
              </Select>
              <Select value={itemCategory || '__ALL__'} onValueChange={(v) => setItemCategory(v === '__ALL__' ? '' : v)}>
                <SelectTrigger className="bg-[#18181B] border-white/10 h-8 text-xs w-[160px]" data-testid="filter-category">
                  <SelectValue placeholder="All categories" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__ALL__">All categories</SelectItem>
                  {(itemFilters?.categories || []).map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
              <Select value={filterPartner || '__ALL__'} onValueChange={(v) => { setFilterPartner(v === '__ALL__' ? '' : v); }}>
                <SelectTrigger className="bg-[#18181B] border-white/10 h-8 text-xs w-[140px]" data-testid="item-filter-partner">
                  <SelectValue placeholder="All partners" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__ALL__">All partners</SelectItem>
                  {partners.filter(p => p.partner_id !== 'unknown').map((p) => (
                    <SelectItem key={p.partner_id} value={p.partner_id}>{p.partner_name}</SelectItem>
                  ))}
                  <SelectItem value="unknown">Unknown</SelectItem>
                </SelectContent>
              </Select>
              {hasItemFilters && (
                <button onClick={() => { setItemBucket(''); setItemCategory(''); setItemSearch(''); setFilterPartner(''); }} className="flex items-center gap-1 text-xs text-zinc-500 hover:text-zinc-300 transition-colors" data-testid="clear-item-filters">
                  <X size={12} /> Clear
                </button>
              )}
            </div>
          </div>

          {/* Sub-navigation (hidden during detail drilldown) */}
          {itemSubView !== 'detail' && (
            <div className="flex items-center gap-3 flex-wrap">
              <div className="flex gap-1 bg-white/[0.02] rounded-lg p-1 w-fit" data-testid="item-sub-nav">
                {[
                  { key: 'top', label: 'Top Items', icon: TrendUp },
                  { key: 'categories', label: 'Categories', icon: Tag },
                  { key: 'movers', label: 'Movers', icon: Lightning },
                  { key: 'trends', label: 'Daily Trends', icon: ChartBar },
                  { key: 'weekday', label: 'Day of Week', icon: Clock },
                  { key: 'hour', label: 'Items by Hour', icon: Clock },
                ].map((t) => {
                  const Icon = t.icon;
                  const active = itemSubView === t.key;
                  return (
                    <button
                      key={t.key}
                      onClick={() => setItemSubView(t.key)}
                      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${active ? 'bg-white/10 text-white' : 'text-zinc-500 hover:text-zinc-300'}`}
                      data-testid={`item-sub-${t.key}`}
                    >
                      <Icon size={14} /> {t.label}
                    </button>
                  );
                })}
              </div>
              {/* Grain badge next to sub-nav */}
              <GrainBadge grainSummary={topItems?.grain_summary || categoryData?.grain_summary || moversData?.grain_summary || dailyTrendData?.grain_summary || weekdayData?.grain_summary || itemFilters?.grain_summary} />
            </div>
          )}

          {itemsLoading ? (
            <div className="py-16 text-center text-zinc-500">Loading item intelligence...</div>
          ) : (
            <>
              {/* ─── TOP ITEMS VIEW ─── */}
              {itemSubView === 'top' && topItems && (
                <>
                  <ItemSummaryCards data={topItems} />
                  <TopItemsTable
                    items={topItems.items}
                    comparison={topItems.comparison}
                    sortBy={itemSortBy}
                    onSortChange={setItemSortBy}
                    onDrillIn={drillIntoItem}
                  />
                </>
              )}
              {itemSubView === 'top' && !topItems && <EmptyItemState />}

              {/* ─── CATEGORIES VIEW ─── */}
              {itemSubView === 'categories' && <CategoryAnalyticsView data={categoryData} />}

              {/* ─── MOVERS VIEW ─── */}
              {itemSubView === 'movers' && moversData && <MoversView data={moversData} onDrillIn={drillIntoItem} />}
              {itemSubView === 'movers' && !moversData && <EmptyItemState />}

              {/* ─── DAILY TRENDS VIEW ─── */}
              {itemSubView === 'trends' && <DailyTrendView data={dailyTrendData} />}

              {/* ─── WEEKDAY VIEW ─── */}
              {itemSubView === 'weekday' && (
                <WeekdayView
                  data={weekdayData}
                  filters={{
                    outlet_id: filterOutlet || undefined,
                    revenue_bucket: itemBucket || undefined,
                    category: itemCategory || undefined,
                    search: itemSearch || undefined,
                    partner_id: filterPartner || undefined,
                    ...getTimeParams(),
                  }}
                />
              )}

              {/* ─── ITEMS BY HOUR VIEW ─── */}
              {itemSubView === 'hour' && (
                <ItemsByHourView
                  baseParams={{
                    outlet_id: filterOutlet || undefined,
                    category: itemCategory || undefined,
                    partner_id: filterPartner || undefined,
                    include_comparison: true,
                    ...getTimeParams(),
                  }}
                />
              )}

              {/* ─── ITEM DETAIL DRILLDOWN ─── */}
              {itemSubView === 'detail' && <ItemDetailPanel detail={itemDetail} txnContext={txnContext} loading={itemsLoading} onBack={backToTopItems} />}
            </>
          )}
        </div>
      )}
    </div>
  );
};

/* ═══════ HOURLY SUMMARY ═══════ */
const HourlySummary = ({ data }) => {
  if (!data.length) return null;
  const peakRevenue = data.reduce((a, b) => a.net_sales > b.net_sales ? a : b);
  const peakOrders = data.reduce((a, b) => a.order_count > b.order_count ? a : b);
  const avgBillPeak = data.reduce((a, b) => a.avg_bill > b.avg_bill ? a : b);
  return (
    <div className="grid grid-cols-3 gap-4" data-testid="hourly-summary">
      <div className="surface-base rounded-lg p-4">
        <p className="text-[10px] uppercase tracking-widest text-zinc-500">Peak Revenue Hour</p>
        <p className="text-2xl font-mono font-light text-cyan-400 mt-1">{peakRevenue.label}</p>
        <p className="text-xs text-zinc-400 mt-0.5">{fmtCurrency(peakRevenue.net_sales)} net | {peakRevenue.order_count} orders</p>
      </div>
      <div className="surface-base rounded-lg p-4">
        <p className="text-[10px] uppercase tracking-widest text-zinc-500">Peak Crowd Hour</p>
        <p className="text-2xl font-mono font-light text-[#D4FF00] mt-1">{peakOrders.label}</p>
        <p className="text-xs text-zinc-400 mt-0.5">{peakOrders.order_count} orders | {fmtCurrency(peakOrders.net_sales)} net</p>
      </div>
      <div className="surface-base rounded-lg p-4">
        <p className="text-[10px] uppercase tracking-widest text-zinc-500">Highest Avg Bill</p>
        <p className="text-2xl font-mono font-light text-violet-400 mt-1">{avgBillPeak.label}</p>
        <p className="text-xs text-zinc-400 mt-0.5">{fmtCurrency(avgBillPeak.avg_bill)} avg | {avgBillPeak.order_count} orders</p>
      </div>
    </div>
  );
};

/* ═══════ ITEM INTELLIGENCE COMPONENTS ═══════ */

const BUCKET_COLORS = {
  food: 'bg-emerald-500/10 text-emerald-400',
  beverage: 'bg-cyan-500/10 text-cyan-400',
  liquor: 'bg-violet-500/10 text-violet-400',
};
const bucketBadge = (b) => BUCKET_COLORS[b?.toLowerCase()] || 'bg-zinc-500/10 text-zinc-400';
const PIE_COLORS = ['#06B6D4', '#D4FF00', '#8B5CF6', '#F59E0B', '#EC4899', '#10B981', '#6366F1', '#F97316'];

const EmptyItemState = () => (
  <div className="surface-base rounded-lg py-16 text-center" data-testid="items-empty">
    <Package size={48} className="mx-auto text-zinc-700 mb-4" />
    <p className="text-zinc-500 text-sm">No item data found</p>
    <p className="text-zinc-600 text-xs mt-1">Upload item-level sales data to see demand intelligence</p>
  </div>
);

const ItemSummaryCards = ({ data }) => {
  const { items, total_net_sales, total_quantity, unique_item_count } = data;
  if (!items?.length) return null;
  const topByQty = items.reduce((a, b) => (a.quantity_sold > b.quantity_sold ? a : b), items[0]);
  const topByRev = items.reduce((a, b) => (a.net_sales > b.net_sales ? a : b), items[0]);
  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="item-summary-cards">
      <div className="surface-base rounded-lg p-4">
        <p className="text-[10px] uppercase tracking-widest text-zinc-500">Total Qty Sold</p>
        <p className="text-2xl font-mono font-light text-white mt-1">{fmtShort(total_quantity)}</p>
        <p className="text-xs text-zinc-500 mt-0.5">{unique_item_count || items.length} unique items</p>
      </div>
      <div className="surface-base rounded-lg p-4">
        <p className="text-[10px] uppercase tracking-widest text-zinc-500">Item Revenue (rolled up)</p>
        <p className="text-2xl font-mono font-light text-cyan-400 mt-1">{fmtCurrency(total_net_sales)}</p>
      </div>
      <div className="surface-base rounded-lg p-4">
        <p className="text-[10px] uppercase tracking-widest text-zinc-500">Top Item (Qty)</p>
        <p className="text-base font-mono font-light text-[#D4FF00] mt-1 truncate">{topByQty?.item_name}</p>
        <p className="text-xs text-zinc-400 mt-0.5">{topByQty?.quantity_sold} units sold</p>
      </div>
      <div className="surface-base rounded-lg p-4">
        <p className="text-[10px] uppercase tracking-widest text-zinc-500">Top Item (Revenue)</p>
        <p className="text-base font-mono font-light text-violet-400 mt-1 truncate">{topByRev?.item_name}</p>
        <p className="text-xs text-zinc-400 mt-0.5">{fmtCurrency(topByRev?.net_sales)} net</p>
      </div>
    </div>
  );
};

const SORT_OPTIONS = [
  { key: 'quantity', label: 'Qty Sold' },
  { key: 'revenue', label: 'Line Revenue' },
  { key: 'final', label: 'Final Sales' },
  { key: 'avg_price', label: 'Avg Price' },
];

const TopItemsTable = ({ items, comparison, sortBy, onSortChange, onDrillIn }) => {
  if (!items?.length) return <EmptyItemState />;
  const compareTitle = comparison
    ? `${comparison.label} (${comparison.previous_from} → ${comparison.previous_to})`
    : undefined;
  return (
    <div className="surface-base rounded-lg overflow-hidden" data-testid="top-items-table">
      <div className="px-4 py-2.5 border-b border-white/5 flex items-center gap-2 flex-wrap">
        <span className="text-[10px] uppercase tracking-widest text-zinc-500 mr-1">Sort by</span>
        {SORT_OPTIONS.map((s) => (
          <button
            key={s.key}
            onClick={() => onSortChange(s.key)}
            className={`text-xs px-2.5 py-1 rounded transition-colors ${sortBy === s.key ? 'bg-[#D4FF00]/10 text-[#D4FF00]' : 'text-zinc-500 hover:text-zinc-300'}`}
            data-testid={`sort-${s.key}`}
          >
            {s.label}
          </button>
        ))}
        {comparison && (
          <span
            className="ml-auto text-[10px] font-mono text-zinc-500"
            data-testid="top-items-compare-label"
            title={compareTitle}
          >{comparison.label}</span>
        )}
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-xs" data-testid="items-data-table">
          <thead>
            <tr className="bg-zinc-800/50 text-zinc-400 text-left">
              <th className="p-3 font-mono w-8">#</th>
              <th className="p-3 font-mono">Item</th>
              <th className="p-3 font-mono">Bucket</th>
              <th className="p-3 font-mono">Category</th>
              <th className="p-3 font-mono text-right">Qty</th>
              <th className="p-3 font-mono text-right">Line Revenue</th>
              <th className="p-3 font-mono text-right">Final</th>
              <th className="p-3 font-mono text-right">Avg Price</th>
              <th className="p-3 font-mono text-right">Peak Hour</th>
              <th className="p-3 font-mono text-right">Contribution</th>
            </tr>
          </thead>
          <tbody>
            {items.map((it, idx) => (
              <tr
                key={it.item_name_normalized}
                className="border-t border-white/5 hover:bg-white/[0.02] cursor-pointer transition-colors"
                onClick={() => onDrillIn(it.item_name_normalized)}
                data-testid={`item-row-${idx}`}
              >
                <td className="p-3 font-mono text-zinc-600">{idx + 1}</td>
                <td className="p-3">
                  <div className="font-mono text-zinc-200 truncate max-w-[220px]">{it.item_name}</div>
                  {it.source_item_code && <div className="text-[10px] text-zinc-600 mt-0.5">{it.source_item_code}</div>}
                </td>
                <td className="p-3"><span className={`text-[10px] px-1.5 py-0.5 rounded ${bucketBadge(it.revenue_bucket)}`}>{it.revenue_bucket || '\u2014'}</span></td>
                <td className="p-3 font-mono text-zinc-400">{it.category || '\u2014'}</td>
                <td className="p-3 font-mono text-right text-zinc-200">
                  <div className="inline-flex items-center gap-1.5">
                    <span>{it.quantity_sold}</span>
                    <DeltaPill
                      direction={it.quantity_change_direction}
                      pct={it.quantity_change_pct}
                      title={compareTitle && (it.previous_quantity != null
                        ? `${compareTitle} · prev qty ${it.previous_quantity}`
                        : compareTitle)}
                      testId={`top-item-delta-${idx}`}
                    />
                  </div>
                </td>
                <td className="p-3 font-mono text-right text-zinc-200">{fmtCurrency(it.net_sales)}</td>
                <td className="p-3 font-mono text-right text-zinc-300">{fmtCurrency(it.final_sales)}</td>
                <td className="p-3 font-mono text-right text-[#D4FF00]">{fmtCurrency(it.avg_selling_price)}</td>
                <td className="p-3 font-mono text-right" data-testid={`item-peak-hour-${idx}`}>
                  {it.peak_hour != null ? (
                    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-orange-500/10 text-orange-300 border border-orange-500/20 text-[10px]">
                      {(() => {
                        const h = it.peak_hour;
                        const ampm = h < 12 ? 'AM' : 'PM';
                        const disp = h === 0 ? 12 : h > 12 ? h - 12 : h;
                        return `${disp} ${ampm}`;
                      })()}
                    </span>
                  ) : (
                    <span className="text-zinc-600">—</span>
                  )}
                </td>
                <td className="p-3">
                  <div className="flex items-center justify-end gap-2">
                    <div className="w-16 h-1.5 bg-zinc-800 rounded-full overflow-hidden">
                      <div className="h-full bg-cyan-500/80 rounded-full" style={{ width: `${Math.min(it.revenue_contribution_pct, 100)}%` }} />
                    </div>
                    <span className="font-mono text-zinc-300 w-10 text-right">{it.revenue_contribution_pct}%</span>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const CategoryAnalyticsView = ({ data }) => {
  // data is now { buckets: [...], grain_summary: {...}, comparison: {...} }
  // or legacy array
  const buckets = data?.buckets || (Array.isArray(data) ? data : []);
  const grainSummary = data?.grain_summary;
  const comparison = data?.comparison;
  const compareTitle = comparison
    ? `${comparison.label} (${comparison.previous_from} → ${comparison.previous_to})`
    : undefined;
  if (!buckets?.length) return (
    <div className="surface-base rounded-lg py-16 text-center" data-testid="categories-empty">
      <Tag size={48} className="mx-auto text-zinc-700 mb-4" />
      <p className="text-zinc-500 text-sm">No category data available</p>
    </div>
  );
  const totalNet = buckets.reduce((s, d) => s + d.net_sales, 0);
  return (
    <div className="space-y-4" data-testid="category-view">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="surface-base rounded-lg p-5">
          <h3 className="text-sm font-medium text-zinc-300 mb-3 flex items-center gap-2">
            <Tag size={16} className="text-violet-400" /> Revenue by Category
          </h3>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={buckets}
                  dataKey="net_sales"
                  nameKey="bucket"
                  cx="50%"
                  cy="50%"
                  outerRadius={90}
                  innerRadius={45}
                  label={({ bucket, revenue_share_pct }) => `${bucket} ${revenue_share_pct}%`}
                  labelLine={true}
                  fontSize={11}
                >
                  {buckets.map((b, i) => <Cell key={b.bucket} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                </Pie>
                <Tooltip
                  contentStyle={{ backgroundColor: '#18181B', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px', fontSize: '12px' }}
                  formatter={(v) => [fmtCurrency(v), 'Net Sales']}
                />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>
        <div className="surface-base rounded-lg p-5">
          <h3 className="text-sm font-medium text-zinc-300 mb-3 flex items-center gap-2">
            <ChartBar size={16} className="text-cyan-400" /> Quantity by Category
          </h3>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={buckets} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke="#27272A" />
                <XAxis type="number" stroke="#71717A" fontSize={11} />
                <YAxis dataKey="bucket" type="category" stroke="#71717A" fontSize={11} width={80} />
                <Tooltip contentStyle={{ backgroundColor: '#18181B', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px', fontSize: '12px' }} />
                <Bar dataKey="quantity_sold" fill="#D4FF00" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>
      <div className="surface-base rounded-lg overflow-hidden">
        {comparison && (
          <div className="px-3 py-2 border-b border-white/5 flex justify-end">
            <span
              className="text-[10px] font-mono text-zinc-500"
              data-testid="category-compare-label"
              title={compareTitle}
            >{comparison.label}</span>
          </div>
        )}
        <table className="w-full text-xs" data-testid="category-table">
          <thead>
            <tr className="bg-zinc-800/50 text-zinc-400 text-left">
              <th className="p-3 font-mono">Category</th>
              <th className="p-3 font-mono text-right">Unique Items</th>
              <th className="p-3 font-mono text-right">Total Qty</th>
              <th className="p-3 font-mono text-right">Net Sales</th>
              <th className="p-3 font-mono text-right">Final Sales</th>
              <th className="p-3 font-mono text-right">Tax</th>
              <th className="p-3 font-mono text-right">Revenue Share</th>
            </tr>
          </thead>
          <tbody>
            {buckets.map((d, idx) => (
              <tr key={d.bucket} className="border-t border-white/5">
                <td className="p-3"><span className={`text-xs px-2 py-0.5 rounded ${bucketBadge(d.bucket)}`}>{d.bucket}</span></td>
                <td className="p-3 font-mono text-right text-zinc-300">{d.unique_item_count}</td>
                <td className="p-3 font-mono text-right text-zinc-200">
                  <div className="inline-flex items-center gap-1.5">
                    <span>{d.quantity_sold}</span>
                    <DeltaPill
                      direction={d.quantity_change_direction}
                      pct={d.quantity_change_pct}
                      title={compareTitle && (d.previous_quantity != null
                        ? `${compareTitle} · prev qty ${d.previous_quantity}`
                        : compareTitle)}
                      testId={`category-delta-${idx}`}
                    />
                  </div>
                </td>
                <td className="p-3 font-mono text-right text-zinc-200">{fmtCurrency(d.net_sales)}</td>
                <td className="p-3 font-mono text-right text-zinc-300">{fmtCurrency(d.final_sales)}</td>
                <td className="p-3 font-mono text-right text-zinc-400">{fmtCurrency(d.tax_amount)}</td>
                <td className="p-3">
                  <div className="flex items-center justify-end gap-2">
                    <div className="w-20 h-1.5 bg-zinc-800 rounded-full overflow-hidden">
                      <div className="h-full bg-violet-500/80 rounded-full" style={{ width: `${d.revenue_share_pct}%` }} />
                    </div>
                    <span className="font-mono text-zinc-300 w-12 text-right">{d.revenue_share_pct}%</span>
                  </div>
                </td>
              </tr>
            ))}
            <tr className="border-t border-white/10 bg-white/[0.02]">
              <td className="p-3 font-mono text-zinc-200 font-medium">Total</td>
              <td className="p-3 font-mono text-right text-zinc-200 font-medium">{buckets.reduce((s, d) => s + d.unique_item_count, 0)}</td>
              <td className="p-3 font-mono text-right text-zinc-200 font-medium">{buckets.reduce((s, d) => s + d.quantity_sold, 0)}</td>
              <td className="p-3 font-mono text-right text-white font-medium">{fmtCurrency(totalNet)}</td>
              <td className="p-3 font-mono text-right text-zinc-200 font-medium">{fmtCurrency(buckets.reduce((s, d) => s + d.final_sales, 0))}</td>
              <td className="p-3 font-mono text-right text-zinc-300 font-medium">{fmtCurrency(buckets.reduce((s, d) => s + d.tax_amount, 0))}</td>
              <td className="p-3 font-mono text-right text-zinc-300 font-medium">100%</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
};

const MoversView = ({ data, onDrillIn }) => {
  const compareTitle = data?.comparison
    ? `${data.comparison.label} (${data.comparison.previous_from} → ${data.comparison.previous_to})`
    : undefined;
  return (
    <div className="space-y-3" data-testid="movers-view">
      {data?.comparison && (
        <div
          className="flex justify-end text-[10px] font-mono text-zinc-500"
          data-testid="movers-compare-label"
          title={compareTitle}
        >quantity change {data.comparison.label}</div>
      )}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <MoverPanel title={'Fast Movers \u2014 Quantity'} subtitle="Highest volume items" icon={TrendUp} color="text-emerald-400" accentBg="bg-emerald-500/10" items={data.fast_by_quantity} metric="quantity_sold" metricLabel="qty" onDrillIn={onDrillIn} compareTitle={compareTitle} testIdGroup="fast-qty" />
        <MoverPanel title={'Fast Movers \u2014 Revenue'} subtitle="Highest revenue items" icon={CurrencyInr} color="text-cyan-400" accentBg="bg-cyan-500/10" items={data.fast_by_revenue} metric="net_sales" metricLabel="net" onDrillIn={onDrillIn} compareTitle={compareTitle} testIdGroup="fast-rev" />
        <MoverPanel title="Slow Movers" subtitle="Lowest volume items" icon={TrendDown} color="text-amber-400" accentBg="bg-amber-500/10" items={data.slow_movers} metric="quantity_sold" metricLabel="qty" onDrillIn={onDrillIn} compareTitle={compareTitle} testIdGroup="slow" />
        <MoverPanel title="High-Value Low-Frequency" subtitle="Premium items with low volume" icon={Lightning} color="text-violet-400" accentBg="bg-violet-500/10" items={data.high_value_low_frequency} metric="avg_price" metricLabel="net" onDrillIn={onDrillIn} compareTitle={compareTitle} testIdGroup="hvlf" />
      </div>
    </div>
  );
};

const MoverPanel = ({ title, subtitle, icon: Icon, color, accentBg, items, metric, metricLabel, onDrillIn, compareTitle, testIdGroup }) => (
  <div className="surface-base rounded-lg overflow-hidden" data-testid={`mover-panel`}>
    <div className="px-4 py-3 border-b border-white/5 flex items-center gap-2">
      <div className={`p-1.5 rounded ${accentBg}`}><Icon size={14} className={color} /></div>
      <div>
        <p className="text-xs font-medium text-zinc-200">{title}</p>
        <p className="text-[10px] text-zinc-500">{subtitle}</p>
      </div>
    </div>
    <div className="divide-y divide-white/5">
      {items.map((it, idx) => (
        <div
          key={it.item_name_normalized}
          className="px-4 py-2.5 flex items-center gap-3 hover:bg-white/[0.02] cursor-pointer transition-colors"
          onClick={() => onDrillIn(it.item_name_normalized)}
          data-testid={`mover-item-${idx}`}
        >
          <span className="text-[10px] font-mono text-zinc-600 w-5">{idx + 1}</span>
          <div className="flex-1 min-w-0">
            <p className="text-xs text-zinc-200 truncate">{it.item_name}</p>
            <p className="text-[10px] text-zinc-500">{it.revenue_bucket}{it.category ? ` \u00b7 ${it.category}` : ''}</p>
          </div>
          <div className="text-right">
            <div className="flex items-center justify-end gap-1.5">
              <p className={`text-xs font-mono ${color}`}>
                {metricLabel === 'qty' ? it[metric] : fmtCurrency(it[metric])}
              </p>
              {it.quantity_change_direction && (
                <DeltaPill
                  direction={it.quantity_change_direction}
                  pct={it.quantity_change_pct}
                  title={compareTitle && (it.previous_quantity != null
                    ? `${compareTitle} · prev qty ${it.previous_quantity}`
                    : compareTitle)}
                  testId={`mover-delta-${testIdGroup}-${idx}`}
                />
              )}
            </div>
            <p className="text-[10px] font-mono text-zinc-500">
              {metricLabel === 'qty' ? `${fmtCurrency(it.net_sales)} net` : `${it.quantity_sold} qty`}
            </p>
          </div>
        </div>
      ))}
      {items.length === 0 && <div className="px-4 py-6 text-center text-zinc-600 text-xs">No data</div>}
    </div>
  </div>
);

const ItemDetailPanel = ({ detail, txnContext, loading, onBack }) => {
  const [detailTab, setDetailTab] = useState('weekday');
  const [txnExpanded, setTxnExpanded] = useState(false);

  if (loading) return <div className="py-16 text-center text-zinc-500">Loading item detail...</div>;
  if (!detail) return (
    <div className="space-y-4">
      <button onClick={onBack} className="flex items-center gap-1.5 text-xs text-zinc-400 hover:text-white transition-colors" data-testid="item-detail-back">
        <CaretLeft size={14} /> Back to Top Items
      </button>
      <div className="surface-base rounded-lg py-16 text-center text-zinc-500">Item detail not available</div>
    </div>
  );
  const t = detail.totals || {};
  const wd = detail.weekday_breakdown || [];
  const peakWd = wd.length ? wd.reduce((a, b) => a.avg_net_sales > b.avg_net_sales ? a : b, wd[0]) : null;
  const WEEKDAY_COLORS = { Monday: '#06B6D4', Tuesday: '#8B5CF6', Wednesday: '#D4FF00', Thursday: '#F59E0B', Friday: '#EC4899', Saturday: '#10B981', Sunday: '#F97316' };

  return (
    <div className="space-y-4" data-testid="item-detail-panel">
      <div className="flex items-center gap-3">
        <button onClick={onBack} className="flex items-center gap-1.5 text-xs text-zinc-400 hover:text-white transition-colors" data-testid="item-detail-back">
          <CaretLeft size={14} /> Back to Top Items
        </button>
        <GrainBadge grainSummary={detail.grain_summary} />
      </div>

      {/* Item header + totals */}
      <div className="surface-base rounded-lg p-5">
        <div className="flex items-start justify-between mb-4">
          <div>
            <h2 className="text-lg font-mono text-white">{detail.item_name}</h2>
            <div className="flex items-center gap-2 mt-1 flex-wrap">
              {detail.source_item_code && <span className="text-[10px] font-mono text-zinc-500">SKU: {detail.source_item_code}</span>}
              {detail.revenue_bucket && <span className={`text-[10px] px-1.5 py-0.5 rounded ${bucketBadge(detail.revenue_bucket)}`}>{detail.revenue_bucket}</span>}
              {detail.category && <span className="text-[10px] px-1.5 py-0.5 rounded bg-zinc-500/10 text-zinc-400">{detail.category}</span>}
              {detail.subcategory && <span className="text-[10px] text-zinc-600">/ {detail.subcategory}</span>}
            </div>
          </div>
          {peakWd && (
            <div className="text-right">
              <p className="text-[10px] uppercase tracking-widest text-zinc-500">Strongest Day</p>
              <p className="text-sm font-mono text-[#D4FF00]">{peakWd.weekday}</p>
              <p className="text-[10px] text-zinc-500">{fmtCurrency(peakWd.avg_net_sales)} avg</p>
            </div>
          )}
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <DetailStat label="Quantity Sold" value={t.quantity_sold} />
          <DetailStat label="Net Sales" value={fmtCurrency(t.net_sales)} accent="text-cyan-400" />
          <DetailStat label="Final Sales" value={fmtCurrency(t.final_sales)} />
          <DetailStat label="Avg Selling Price" value={fmtCurrency(t.avg_selling_price)} accent="text-[#D4FF00]" />
          <DetailStat label="Tax Amount" value={fmtCurrency(t.tax_amount)} />
          <DetailStat label="Discount Amount" value={fmtCurrency(t.discount_amount)} accent="text-red-400/80" />
          <DetailStat label="Order Count" value={t.order_count} />
          {detail.item_group_name && <DetailStat label="Item Group" value={detail.item_group_name} />}
        </div>
      </div>

      {/* Tabbed: Weekday (default) vs Daily Trend */}
      <div className="surface-base rounded-lg overflow-hidden">
        <div className="flex border-b border-white/5">
          <button
            onClick={() => setDetailTab('weekday')}
            className={`flex items-center gap-1.5 px-4 py-2.5 text-xs font-medium transition-colors border-b-2 ${detailTab === 'weekday' ? 'border-[#D4FF00] text-white' : 'border-transparent text-zinc-500 hover:text-zinc-300'}`}
            data-testid="detail-tab-weekday"
          >
            <Clock size={14} /> Day of Week
          </button>
          <button
            onClick={() => setDetailTab('daily')}
            className={`flex items-center gap-1.5 px-4 py-2.5 text-xs font-medium transition-colors border-b-2 ${detailTab === 'daily' ? 'border-[#D4FF00] text-white' : 'border-transparent text-zinc-500 hover:text-zinc-300'}`}
            data-testid="detail-tab-daily"
          >
            <ChartBar size={14} /> Daily Trend
          </button>
        </div>

        <div className="p-5">
          {/* Day-only grain note for both sub-tabs */}
          {detail.grain_summary?.is_mixed_grain && (detailTab === 'weekday' || detailTab === 'daily') && (
            <div className="mb-4">
              <DayOnlyNote grainSummary={detail.grain_summary} grainNote="Day-of-week and daily trend analysis uses daily data only. Monthly aggregates are excluded from these views." />
            </div>
          )}

          {detailTab === 'weekday' && wd.length > 0 && (
            <div className="space-y-4" data-testid="item-weekday-view">
              <div className="h-48">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={wd}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#27272A" />
                    <XAxis dataKey="weekday" stroke="#71717A" fontSize={10} />
                    <YAxis stroke="#71717A" fontSize={10} tickFormatter={fmtShort} />
                    <Tooltip contentStyle={{ backgroundColor: '#18181B', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px', fontSize: '12px' }} formatter={(v) => [typeof v === 'number' && v > 100 ? fmtCurrency(v) : v]} />
                    <Bar dataKey="avg_net_sales" radius={[4, 4, 0, 0]} name="Avg Net Sales">
                      {wd.map((d) => <Cell key={d.weekday} fill={WEEKDAY_COLORS[d.weekday] || '#8B5CF6'} />)}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-xs" data-testid="item-weekday-table">
                  <thead>
                    <tr className="bg-zinc-800/50 text-zinc-400 text-left">
                      <th className="p-2.5 font-mono">Day</th>
                      <th className="p-2.5 font-mono text-right">Total Qty</th>
                      <th className="p-2.5 font-mono text-right">Avg Qty</th>
                      <th className="p-2.5 font-mono text-right">Total Net</th>
                      <th className="p-2.5 font-mono text-right">Avg Net</th>
                      <th className="p-2.5 font-mono text-right">Total Final</th>
                      <th className="p-2.5 font-mono text-right">Days</th>
                    </tr>
                  </thead>
                  <tbody>
                    {wd.map((d) => (
                      <tr key={d.weekday} className={`border-t border-white/5 ${d.weekday === peakWd?.weekday ? 'bg-[#D4FF00]/[0.03]' : ''}`}>
                        <td className="p-2.5 font-mono text-zinc-200">{d.weekday}{d.weekday === peakWd?.weekday ? ' *' : ''}</td>
                        <td className="p-2.5 font-mono text-right text-zinc-200">{d.total_quantity}</td>
                        <td className="p-2.5 font-mono text-right text-cyan-400">{d.avg_quantity}</td>
                        <td className="p-2.5 font-mono text-right text-zinc-200">{fmtCurrency(d.total_net_sales)}</td>
                        <td className="p-2.5 font-mono text-right text-[#D4FF00]">{fmtCurrency(d.avg_net_sales)}</td>
                        <td className="p-2.5 font-mono text-right text-zinc-300">{fmtCurrency(d.total_final_sales)}</td>
                        <td className="p-2.5 font-mono text-right text-zinc-500">{d.days_covered}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
          {detailTab === 'weekday' && !wd.length && (
            <div className="py-10 text-center text-zinc-500 text-xs">No weekday data available for this item</div>
          )}

          {detailTab === 'daily' && detail.daily_trend?.length > 0 && (
            <div className="space-y-4" data-testid="item-daily-view">
              <div className="h-48">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={detail.daily_trend}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#27272A" />
                    <XAxis dataKey="business_date" stroke="#71717A" fontSize={10} />
                    <YAxis yAxisId="qty" orientation="left" stroke="#71717A" fontSize={10} />
                    <YAxis yAxisId="rev" orientation="right" stroke="#71717A" fontSize={10} tickFormatter={fmtShort} />
                    <Tooltip contentStyle={{ backgroundColor: '#18181B', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px', fontSize: '12px' }} />
                    <Bar yAxisId="qty" dataKey="quantity" fill="#D4FF00" radius={[4, 4, 0, 0]} name="Qty" />
                    <Bar yAxisId="rev" dataKey="net_sales" fill="#06B6D4" radius={[4, 4, 0, 0]} name="Net Sales" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="bg-zinc-800/50 text-zinc-400 text-left">
                      <th className="p-2.5 font-mono">Date</th>
                      <th className="p-2.5 font-mono">Day</th>
                      <th className="p-2.5 font-mono text-right">Qty</th>
                      <th className="p-2.5 font-mono text-right">Net Sales</th>
                      <th className="p-2.5 font-mono text-right">Final Sales</th>
                    </tr>
                  </thead>
                  <tbody>
                    {detail.daily_trend.map((d) => (
                      <tr key={d.business_date} className="border-t border-white/5">
                        <td className="p-2.5 font-mono text-zinc-200">{d.business_date}</td>
                        <td className="p-2.5 font-mono text-zinc-400">{d.weekday}</td>
                        <td className="p-2.5 font-mono text-right text-zinc-200">{d.quantity}</td>
                        <td className="p-2.5 font-mono text-right text-zinc-200">{fmtCurrency(d.net_sales)}</td>
                        <td className="p-2.5 font-mono text-right text-zinc-300">{fmtCurrency(d.final_sales)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
          {detailTab === 'daily' && !detail.daily_trend?.length && (
            <div className="py-10 text-center text-zinc-500 text-xs">No daily trend data available</div>
          )}
        </div>
      </div>

      {detail.invoice_type_mix?.length > 0 && (
        <div className="surface-base rounded-lg p-5">
          <h3 className="text-sm font-medium text-zinc-300 mb-3 flex items-center gap-2">
            <Storefront size={16} className="text-violet-400" /> Invoice Type Breakdown
          </h3>
          <div className="overflow-x-auto">
            <table className="w-full text-xs" data-testid="item-invoice-type-mix">
              <thead>
                <tr className="text-zinc-400 text-left">
                  <th className="p-2 font-mono">Type</th>
                  <th className="p-2 font-mono text-right">Qty</th>
                  <th className="p-2 font-mono text-right">Net Sales</th>
                  <th className="p-2 font-mono text-right">Count</th>
                  <th className="p-2 font-mono text-right">Share</th>
                </tr>
              </thead>
              <tbody>
                {detail.invoice_type_mix.map((c) => {
                  const share = t.net_sales > 0 ? ((c.net_sales / t.net_sales) * 100).toFixed(1) : 0;
                  return (
                    <tr key={c.invoice_type} className="border-t border-white/5">
                      <td className="p-2 font-mono text-zinc-200">{c.invoice_type}</td>
                      <td className="p-2 font-mono text-right text-zinc-300">{c.quantity}</td>
                      <td className="p-2 font-mono text-right text-zinc-200">{fmtCurrency(c.net_sales)}</td>
                      <td className="p-2 font-mono text-right text-zinc-400">{c.order_count}</td>
                      <td className="p-2 font-mono text-right text-[#D4FF00]">{share}%</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Transaction context — collapsible, de-emphasized */}
      {txnContext && txnContext.channels?.length > 0 && (
        <div className="surface-base rounded-lg overflow-hidden border border-zinc-800" data-testid="transaction-context-panel">
          <button
            onClick={() => setTxnExpanded(!txnExpanded)}
            className="w-full px-5 py-3 flex items-center justify-between text-left hover:bg-white/[0.02] transition-colors"
            data-testid="txn-context-toggle"
          >
            <div className="flex items-center gap-2">
              <Storefront size={14} className="text-zinc-500" />
              <span className="text-xs font-medium text-zinc-400">{txnContext.context_label}</span>
              <span className="text-[10px] px-1.5 py-0.5 rounded bg-zinc-800 text-zinc-500">Reference Only</span>
            </div>
            <CaretDown size={14} className={`text-zinc-500 transition-transform ${txnExpanded ? 'rotate-180' : ''}`} />
          </button>
          {txnExpanded && (
            <div className="px-5 pb-4">
              <p className="text-[10px] text-zinc-600 mb-3">{txnContext.disclaimer}</p>
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="text-zinc-500 text-left">
                      <th className="p-2 font-mono">Channel</th>
                      <th className="p-2 font-mono text-right">Orders</th>
                      <th className="p-2 font-mono text-right">Net Sales</th>
                      <th className="p-2 font-mono text-right">Share</th>
                    </tr>
                  </thead>
                  <tbody>
                    {txnContext.channels.map((c) => (
                      <tr key={c.channel} className="border-t border-white/5">
                        <td className="p-2 font-mono text-zinc-400">{c.channel}</td>
                        <td className="p-2 font-mono text-right text-zinc-500">{c.order_count}</td>
                        <td className="p-2 font-mono text-right text-zinc-400">{fmtCurrency(c.net_sales)}</td>
                        <td className="p-2 font-mono text-right text-zinc-500">{c.revenue_share_pct}%</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const DetailStat = ({ label, value, accent }) => (
  <div className="bg-white/[0.02] rounded-lg p-3" data-testid={`detail-stat-${label.toLowerCase().replace(/\s+/g, '-')}`}>
    <p className="text-[10px] uppercase tracking-widest text-zinc-500">{label}</p>
    <p className={`text-base font-mono font-light mt-1 ${accent || 'text-zinc-200'}`}>{value}</p>
  </div>
);

const DailyTrendView = ({ data }) => {
  // data is now { days: [...], grain_summary: {...}, grain_note: "..." } or legacy array
  const days = data?.days || (Array.isArray(data) ? data : []);
  const grainSummary = data?.grain_summary;
  const grainNote = data?.grain_note;

  if (!days?.length) return (
    <div className="surface-base rounded-lg py-16 text-center" data-testid="trends-empty">
      <ChartBar size={48} className="mx-auto text-zinc-700 mb-4" />
      <p className="text-zinc-500 text-sm">No daily trend data available</p>
      <p className="text-zinc-600 text-xs mt-1">Upload item data with date information to see trends</p>
    </div>
  );
  const totalQty = days.reduce((s, d) => s + d.total_quantity, 0);
  const totalNet = days.reduce((s, d) => s + d.total_net_sales, 0);
  const peakDay = days.reduce((a, b) => a.total_net_sales > b.total_net_sales ? a : b, days[0]);
  return (
    <div className="space-y-4" data-testid="daily-trend-view">
      <DayOnlyNote grainSummary={grainSummary} grainNote={grainNote} />
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <div className="surface-base rounded-lg p-4">
          <p className="text-[10px] uppercase tracking-widest text-zinc-500">Period</p>
          <p className="text-base font-mono font-light text-white mt-1">{days.length} days</p>
          <p className="text-xs text-zinc-500 mt-0.5">{days[0]?.business_date} - {days[days.length - 1]?.business_date}</p>
        </div>
        <div className="surface-base rounded-lg p-4">
          <p className="text-[10px] uppercase tracking-widest text-zinc-500">Total Qty</p>
          <p className="text-2xl font-mono font-light text-white mt-1">{fmtShort(totalQty)}</p>
        </div>
        <div className="surface-base rounded-lg p-4">
          <p className="text-[10px] uppercase tracking-widest text-zinc-500">Total Net Sales</p>
          <p className="text-2xl font-mono font-light text-cyan-400 mt-1">{fmtCurrency(totalNet)}</p>
        </div>
        <div className="surface-base rounded-lg p-4">
          <p className="text-[10px] uppercase tracking-widest text-zinc-500">Peak Day</p>
          <p className="text-base font-mono font-light text-[#D4FF00] mt-1">{peakDay?.weekday}</p>
          <p className="text-xs text-zinc-400 mt-0.5">{peakDay?.business_date} - {fmtCurrency(peakDay?.total_net_sales)}</p>
        </div>
      </div>
      <div className="surface-base rounded-lg p-5">
        <h3 className="text-sm font-medium text-zinc-300 mb-3 flex items-center gap-2">
          <ChartBar size={16} className="text-cyan-400" /> Daily Quantity + Revenue
        </h3>
        <div className="h-64">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={days}>
              <CartesianGrid strokeDasharray="3 3" stroke="#27272A" />
              <XAxis dataKey="business_date" stroke="#71717A" fontSize={10} />
              <YAxis yAxisId="qty" orientation="left" stroke="#71717A" fontSize={10} />
              <YAxis yAxisId="rev" orientation="right" stroke="#71717A" fontSize={10} tickFormatter={fmtShort} />
              <Tooltip contentStyle={{ backgroundColor: '#18181B', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px', fontSize: '12px' }} />
              <Bar yAxisId="qty" dataKey="total_quantity" fill="#D4FF00" radius={[4, 4, 0, 0]} name="Qty" />
              <Bar yAxisId="rev" dataKey="total_net_sales" fill="#06B6D4" radius={[4, 4, 0, 0]} name="Net Sales" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
      <div className="surface-base rounded-lg overflow-hidden">
        <table className="w-full text-xs" data-testid="daily-trend-table">
          <thead>
            <tr className="bg-zinc-800/50 text-zinc-400 text-left">
              <th className="p-3 font-mono">Date</th>
              <th className="p-3 font-mono">Day</th>
              <th className="p-3 font-mono text-right">Qty</th>
              <th className="p-3 font-mono text-right">Net Sales</th>
              <th className="p-3 font-mono text-right">Final Sales</th>
              <th className="p-3 font-mono text-right">Unique Items</th>
            </tr>
          </thead>
          <tbody>
            {days.map((d) => (
              <tr key={d.business_date} className="border-t border-white/5">
                <td className="p-3 font-mono text-zinc-200">{d.business_date}</td>
                <td className="p-3 font-mono text-zinc-400">{d.weekday}</td>
                <td className="p-3 font-mono text-right text-zinc-200">{d.total_quantity}</td>
                <td className="p-3 font-mono text-right text-zinc-200">{fmtCurrency(d.total_net_sales)}</td>
                <td className="p-3 font-mono text-right text-zinc-300">{fmtCurrency(d.total_final_sales)}</td>
                <td className="p-3 font-mono text-right text-[#D4FF00]">{d.unique_item_count}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const WEEKDAY_COLORS = {
  Monday: '#06B6D4', Tuesday: '#8B5CF6', Wednesday: '#D4FF00', Thursday: '#F59E0B',
  Friday: '#EC4899', Saturday: '#10B981', Sunday: '#F97316',
};

const WEEKDAY_TOP_COLUMNS = [
  { key: 'rank', label: '#', align: 'left', sortable: false },
  { key: 'item_name', label: 'Item', align: 'left', sortable: false },
  { key: 'quantity_sold', label: 'Qty Sold', align: 'right', sortable: true },
  { key: 'total_line_revenue', label: 'Total Line Revenue', align: 'right', sortable: true, currency: true },
  { key: 'days_covered', label: 'Days Covered', align: 'right', sortable: false },
  { key: 'avg_daily_quantity', label: 'Avg Daily Qty', align: 'right', sortable: true },
  { key: 'avg_daily_line_revenue', label: 'Avg Daily Line Revenue', align: 'right', sortable: true, currency: true },
];

const WeekdayTopItemsPanel = ({ weekday, data, loading, sortBy, onSortChange, onClose }) => {
  return (
    <div
      className="surface-base rounded-lg overflow-hidden border border-[#D4FF00]/20 bg-[#D4FF00]/[0.02] animate-[slideDown_200ms_ease-out]"
      data-testid="weekday-top-items-panel"
    >
      <div className="flex items-center justify-between px-5 py-3 border-b border-white/5">
        <div className="flex items-center gap-3">
          <span
            className="inline-block w-2 h-2 rounded-full"
            style={{ backgroundColor: WEEKDAY_COLORS[weekday] || '#8B5CF6' }}
          />
          <h3 className="text-sm font-medium text-zinc-200" data-testid="weekday-top-items-title">
            Top 15 Items Sold on {weekday}
          </h3>
          {data && (
            <span className="text-[10px] font-mono text-zinc-500">
              {data.days_covered_total} {weekday}{data.days_covered_total === 1 ? '' : 's'} in range · {data.totals?.unique_item_count ?? 0} unique items
            </span>
          )}
        </div>
        <button
          type="button"
          onClick={onClose}
          className="text-zinc-500 hover:text-zinc-200 transition-colors p-1"
          aria-label="Close top items drilldown"
          data-testid="weekday-top-items-close"
        >
          <X size={14} />
        </button>
      </div>

      {loading && (
        <div className="px-5 py-8 text-center text-xs text-zinc-500" data-testid="weekday-top-items-loading">
          Loading top items for {weekday}…
        </div>
      )}

      {!loading && data && data.items?.length === 0 && (
        <div className="px-5 py-8 text-center text-xs text-zinc-500" data-testid="weekday-top-items-empty">
          No items sold on a {weekday} within the current filters.
        </div>
      )}

      {!loading && data && data.items?.length > 0 && (
        <div className="overflow-x-auto">
          <table className="w-full text-xs" data-testid="weekday-top-items-table">
            <thead>
              <tr className="bg-zinc-800/50 text-zinc-400 text-left">
                {WEEKDAY_TOP_COLUMNS.map((c) => {
                  const active = c.sortable && sortBy === c.key;
                  return (
                    <th
                      key={c.key}
                      className={`p-3 font-mono ${c.align === 'right' ? 'text-right' : ''} ${c.sortable ? 'cursor-pointer select-none hover:text-zinc-200' : ''} ${active ? 'text-[#D4FF00]' : ''}`}
                      onClick={c.sortable ? () => onSortChange(c.key) : undefined}
                      data-testid={`weekday-top-items-col-${c.key}`}
                    >
                      <span className="inline-flex items-center gap-1">
                        {c.label}
                        {c.sortable && (
                          <ArrowsDownUp size={10} className={active ? 'text-[#D4FF00]' : 'text-zinc-600'} />
                        )}
                      </span>
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {data.items.map((it) => (
                <tr
                  key={it.item_name_normalized}
                  className="border-t border-white/5 hover:bg-white/[0.02]"
                  data-testid={`weekday-top-items-row-${it.rank}`}
                >
                  <td className="p-3 font-mono text-zinc-500">{it.rank}</td>
                  <td className="p-3 font-mono text-zinc-100">
                    <div className="flex flex-col">
                      <span>{it.item_name}</span>
                      {it.category && (
                        <span className="text-[10px] text-zinc-500">{it.category}</span>
                      )}
                    </div>
                  </td>
                  <td className="p-3 font-mono text-right text-zinc-200">{it.quantity_sold}</td>
                  <td className="p-3 font-mono text-right text-[#D4FF00]">{fmtCurrency(it.total_line_revenue)}</td>
                  <td className="p-3 font-mono text-right text-zinc-500">{it.days_covered}</td>
                  <td className="p-3 font-mono text-right text-cyan-400">{it.avg_daily_quantity}</td>
                  <td className="p-3 font-mono text-right text-zinc-200">{fmtCurrency(it.avg_daily_line_revenue)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

const WeekdayView = ({ data, filters }) => {
  // data is now { weekdays: [...], grain_summary: {...}, grain_note: "..." } or legacy array
  const weekdays = data?.weekdays || (Array.isArray(data) ? data : []);
  const grainSummary = data?.grain_summary;
  const grainNote = data?.grain_note;

  // Drilldown state
  const [selectedWeekday, setSelectedWeekday] = useState(null);
  const [topItemsData, setTopItemsData] = useState(null);
  const [topItemsLoading, setTopItemsLoading] = useState(false);
  const [topItemsSortBy, setTopItemsSortBy] = useState('total_line_revenue');

  // Stable key so we refetch when parent filters change (string serialize)
  const filtersKey = JSON.stringify(filters || {});

  // Reset drilldown if the underlying filters change
  useEffect(() => {
    setSelectedWeekday(null);
    setTopItemsData(null);
  }, [filtersKey]);

  useEffect(() => {
    if (!selectedWeekday) return;
    let cancelled = false;
    setTopItemsLoading(true);
    salesApi
      .getWeekdayTopItems({
        weekday: selectedWeekday,
        sort_by: topItemsSortBy,
        limit: 15,
        ...(filters || {}),
      })
      .then((res) => { if (!cancelled) setTopItemsData(res.data); })
      .catch(() => { if (!cancelled) { setTopItemsData(null); toast.error('Failed to load top items for ' + selectedWeekday); } })
      .finally(() => { if (!cancelled) setTopItemsLoading(false); });
    return () => { cancelled = true; };
  }, [selectedWeekday, topItemsSortBy, filtersKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleBarClick = (payload) => {
    const name = payload?.weekday || payload?.activePayload?.[0]?.payload?.weekday;
    if (!name) return;
    setSelectedWeekday((prev) => (prev === name ? null : name));
  };

  const handleClose = () => setSelectedWeekday(null);

  if (!weekdays?.length) return (
    <div className="surface-base rounded-lg py-16 text-center" data-testid="weekday-empty">
      <Clock size={48} className="mx-auto text-zinc-700 mb-4" />
      <p className="text-zinc-500 text-sm">No weekday data available</p>
    </div>
  );
  const peakDay = weekdays.reduce((a, b) => a.avg_daily_net_sales > b.avg_daily_net_sales ? a : b, weekdays[0]);
  return (
    <div className="space-y-4" data-testid="weekday-view">
      <DayOnlyNote grainSummary={grainSummary} grainNote={grainNote} />
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-3">
        <div className="surface-base rounded-lg p-4">
          <p className="text-[10px] uppercase tracking-widest text-zinc-500">Best Day (Avg Revenue)</p>
          <p className="text-xl font-mono font-light text-[#D4FF00] mt-1">{peakDay?.weekday}</p>
          <p className="text-xs text-zinc-400 mt-0.5">{fmtCurrency(peakDay?.avg_daily_net_sales)} avg daily net</p>
        </div>
        <div className="surface-base rounded-lg p-4">
          <p className="text-[10px] uppercase tracking-widest text-zinc-500">Best Day (Avg Qty)</p>
          {(() => { const p = weekdays.reduce((a, b) => a.avg_daily_quantity > b.avg_daily_quantity ? a : b, weekdays[0]); return (<><p className="text-xl font-mono font-light text-cyan-400 mt-1">{p?.weekday}</p><p className="text-xs text-zinc-400 mt-0.5">{p?.avg_daily_quantity} avg daily units</p></>); })()}
        </div>
        <div className="surface-base rounded-lg p-4">
          <p className="text-[10px] uppercase tracking-widest text-zinc-500">Weekend vs Weekday</p>
          {(() => {
            const wd = weekdays.filter(d => !['Saturday', 'Sunday'].includes(d.weekday));
            const we = weekdays.filter(d => ['Saturday', 'Sunday'].includes(d.weekday));
            const wdAvg = wd.reduce((s, d) => s + d.avg_daily_net_sales, 0) / Math.max(wd.length, 1);
            const weAvg = we.reduce((s, d) => s + d.avg_daily_net_sales, 0) / Math.max(we.length, 1);
            const diff = weAvg - wdAvg;
            return (<><p className={`text-xl font-mono font-light mt-1 ${diff > 0 ? 'text-emerald-400' : 'text-red-400'}`}>{diff > 0 ? '+' : ''}{fmtCurrency(diff)}</p><p className="text-xs text-zinc-400 mt-0.5">Weekend avg vs weekday avg</p></>);
          })()}
        </div>
      </div>
      <div className="surface-base rounded-lg p-5">
        <h3 className="text-sm font-medium text-zinc-300 mb-1 flex items-center gap-2">
          <ChartBar size={16} className="text-violet-400" /> Average Daily Revenue by Weekday
        </h3>
        <p className="text-[11px] text-zinc-500 mb-3">
          Click a weekday bar to see the top 15 items sold on that day.
        </p>
        <div className="h-56">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={weekdays} onClick={handleBarClick}>
              <CartesianGrid strokeDasharray="3 3" stroke="#27272A" />
              <XAxis dataKey="weekday" stroke="#71717A" fontSize={10} />
              <YAxis stroke="#71717A" fontSize={10} tickFormatter={fmtShort} />
              <Tooltip
                cursor={{ fill: 'rgba(212,255,0,0.04)' }}
                contentStyle={{ backgroundColor: '#18181B', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px', fontSize: '12px' }}
                formatter={(v) => [fmtCurrency(v)]}
              />
              <Bar
                dataKey="avg_daily_net_sales"
                radius={[4, 4, 0, 0]}
                name="Avg Daily Net"
                cursor="pointer"
                onClick={(p) => handleBarClick(p)}
              >
                {weekdays.map((d) => {
                  const isSelected = selectedWeekday === d.weekday;
                  const base = WEEKDAY_COLORS[d.weekday] || '#8B5CF6';
                  return (
                    <Cell
                      key={d.weekday}
                      fill={base}
                      fillOpacity={selectedWeekday && !isSelected ? 0.35 : 1}
                      stroke={isSelected ? '#D4FF00' : 'transparent'}
                      strokeWidth={isSelected ? 2 : 0}
                      data-testid={`weekday-bar-${d.weekday}`}
                    />
                  );
                })}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {selectedWeekday && (
        <WeekdayTopItemsPanel
          weekday={selectedWeekday}
          data={topItemsData}
          loading={topItemsLoading}
          sortBy={topItemsSortBy}
          onSortChange={setTopItemsSortBy}
          onClose={handleClose}
        />
      )}

      <div className="surface-base rounded-lg overflow-hidden">
        <table className="w-full text-xs" data-testid="weekday-table">
          <thead>
            <tr className="bg-zinc-800/50 text-zinc-400 text-left">
              <th className="p-3 font-mono">Day</th>
              <th className="p-3 font-mono text-right">Total Qty</th>
              <th className="p-3 font-mono text-right">Total Net</th>
              <th className="p-3 font-mono text-right">Unique Items</th>
              <th className="p-3 font-mono text-right">Days Covered</th>
              <th className="p-3 font-mono text-right">Avg Daily Qty</th>
              <th className="p-3 font-mono text-right">Avg Daily Net</th>
            </tr>
          </thead>
          <tbody>
            {weekdays.map((d) => (
              <tr
                key={d.weekday}
                className={`border-t border-white/5 cursor-pointer hover:bg-white/[0.02] ${d.weekday === peakDay?.weekday ? 'bg-[#D4FF00]/[0.03]' : ''} ${selectedWeekday === d.weekday ? 'outline outline-1 outline-[#D4FF00]/30' : ''}`}
                onClick={() => handleBarClick({ weekday: d.weekday })}
                data-testid={`weekday-row-${d.weekday}`}
              >
                <td className="p-3 font-mono text-zinc-200">{d.weekday}</td>
                <td className="p-3 font-mono text-right text-zinc-200">{d.total_quantity}</td>
                <td className="p-3 font-mono text-right text-zinc-200">{fmtCurrency(d.total_net_sales)}</td>
                <td className="p-3 font-mono text-right text-zinc-400">{d.unique_item_count}</td>
                <td className="p-3 font-mono text-right text-zinc-500">{d.days_covered}</td>
                <td className="p-3 font-mono text-right text-cyan-400">{d.avg_daily_quantity}</td>
                <td className="p-3 font-mono text-right text-[#D4FF00]">{fmtCurrency(d.avg_daily_net_sales)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

/* ═══════ ORDER ROW ═══════ */
const OrderRow = ({ order: o, expanded, detail, onToggle }) => {
  const statusIcon = {
    PASS: <CheckCircle size={14} weight="fill" className="text-emerald-400" />,
    WARN: <WarningCircle size={14} weight="fill" className="text-amber-400" />,
    FAIL: <XCircle size={14} weight="fill" className="text-red-400" />,
  };

  return (
    <div className={`rounded-lg border transition-colors ${expanded ? 'border-[#D4FF00]/30 bg-white/[0.02]' : 'border-white/5 bg-white/[0.01] hover:bg-white/[0.02]'}`} data-testid={`order-${o.id}`}>
      <div className="px-4 py-3 flex items-center gap-4 cursor-pointer flex-wrap" onClick={onToggle}>
        <div className="flex items-center gap-2 min-w-[120px]">
          {statusIcon[o.validation_status] || <Receipt size={14} className="text-zinc-500" />}
          <span className="font-mono text-sm text-zinc-200" data-testid={`order-id-${o.id}`}>{o.order_id}</span>
        </div>
        <span className="text-xs text-zinc-500 font-mono">{o.business_date}</span>
        {o.channel && <span className="text-xs px-2 py-0.5 rounded bg-white/5 text-zinc-400 font-mono">{o.channel}</span>}
        {o.partner_id && o.partner_id !== 'unknown' && (
          <span className="text-[10px] px-2 py-0.5 rounded bg-cyan-500/10 text-cyan-400 border border-cyan-500/20 font-mono" data-testid={`partner-tag-${o.id}`}>{o.partner_id}</span>
        )}
        {o.source_type === 'pos_integration' && <span className="text-[10px] px-2 py-0.5 rounded bg-blue-500/10 text-blue-400">POS</span>}
        <div className="ml-auto flex items-center gap-5 text-xs font-mono">
          <span className="text-zinc-500">Net <span className="text-zinc-200">{fmtCurrency(o.net_sales)}</span></span>
          <span className="text-zinc-500">Total <span className="text-white">{fmtCurrency(o.final_total)}</span></span>
          <span className="text-zinc-500">Paid <span className="text-zinc-200">{fmtCurrency(o.paid_amount)}</span></span>
          <span className="text-[#D4FF00]">{o.confidence_score}%</span>
        </div>
      </div>

      {expanded && detail && (
        <div className="px-4 pb-4 pt-1 border-t border-white/5">
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 text-xs">
            {/* Financials */}
            <div>
              <p className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1.5 flex items-center gap-1"><CurrencyInr size={12} /> Financials</p>
              <div className="space-y-0.5 font-mono">
                <FRow label="Gross" val={detail.gross_sales} />
                <FRow label="Discounts" val={detail.discounts} negative />
                <FRow label="Net" val={detail.net_sales} bold />
                <FRow label="Charges" val={detail.charges_net} />
                <FRow label="Tax" val={detail.taxes_total} />
                <FRow label="Rounding" val={detail.rounding} />
                <FRow label="Tip" val={detail.tip} />
                <div className="border-t border-white/10 pt-0.5"><FRow label="Final Total" val={detail.final_total} bold /></div>
              </div>
            </div>
            {/* Payments */}
            <div>
              <p className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1.5 flex items-center gap-1"><Bank size={12} /> Payments</p>
              <div className="space-y-0.5 font-mono">
                {detail.payments?.length > 0 ? detail.payments.map((p, i) => (
                  <FRow key={`payment-${p.payment_mode}`} label={p.payment_mode} val={p.amount} />
                )) : <p className="text-zinc-600 italic">None</p>}
                <div className="border-t border-white/10 pt-0.5">
                  <FRow label="Paid" val={detail.paid_amount} bold />
                  <FRow label="Balance" val={detail.balance_due} />
                </div>
              </div>
            </div>
            {/* Tax breakdown */}
            <div>
              <p className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1.5 flex items-center gap-1"><Scales size={12} /> Taxes</p>
              <div className="space-y-0.5 font-mono">
                {detail.tax_breakdown?.length > 0 ? detail.tax_breakdown.map((t, i) => (
                  <FRow key={`tax-${t.tax_type}-${t.rate}`} label={`${t.tax_type} ${t.rate || ''}%`} val={t.tax_amount} />
                )) : <p className="text-zinc-600 italic">None</p>}
              </div>
            </div>
            {/* Revenue split */}
            <div>
              <p className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1.5 flex items-center gap-1"><Tag size={12} /> Revenue Split</p>
              <div className="space-y-0.5 font-mono">
                {detail.revenue_split && Object.entries(detail.revenue_split).filter(([, v]) => v > 0).map(([k, v]) => (
                  <FRow key={k} label={k} val={v} />
                ))}
                {(!detail.revenue_split || Object.values(detail.revenue_split).every((v) => !v)) && <p className="text-zinc-600 italic">None</p>}
              </div>
            </div>
            {/* Service context + charges */}
            <div>
              {detail.service_context && Object.keys(detail.service_context).length > 0 && (
                <div className="mb-3">
                  <p className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1.5 flex items-center gap-1"><Clock size={12} /> Service</p>
                  <div className="space-y-0.5 font-mono text-zinc-400">
                    {detail.service_context.table_id && <div>Table: <span className="text-zinc-200">{detail.service_context.table_name || detail.service_context.table_id}</span></div>}
                    {detail.service_context.covers && <div>Covers: <span className="text-zinc-200">{detail.service_context.covers}</span></div>}
                    {detail.service_context.table_opened_at && <div>Open: <span className="text-zinc-200">{detail.service_context.table_opened_at}</span></div>}
                    {detail.service_context.table_closed_at && <div>Close: <span className="text-zinc-200">{detail.service_context.table_closed_at}</span></div>}
                  </div>
                </div>
              )}
              {detail.charges?.length > 0 && (
                <>
                  <p className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1.5">Charges</p>
                  <div className="space-y-0.5 font-mono">
                    {detail.charges.map((c) => <FRow key={`charge-${c.charge_type}`} label={c.charge_type} val={c.amount} />)}
                  </div>
                </>
              )}
              <div className="mt-2 text-[10px] font-mono text-zinc-600">
                <div>Source: {detail.source_type}</div>
                {detail.source_file_id && <div>File: {detail.source_file_id.substring(0, 8)}...</div>}
                {detail.invoice_type && <div>Type: {detail.invoice_type}</div>}
                {detail.order_source && <div>Origin: {detail.order_source}</div>}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const FRow = ({ label, val, bold, negative }) => (
  <div className="flex justify-between">
    <span className="text-zinc-500">{label}</span>
    <span className={`${bold ? 'text-white font-medium' : 'text-zinc-300'} ${negative && val > 0 ? 'text-red-400/80' : ''}`}>
      {negative && val > 0 ? '-' : ''}{fmtCurrency(val)}
    </span>
  </div>
);

export default SalesExplorerPage;
